// ════════════════════════════════════════════════════════════════════════════
//  ManDana — LiveMarkdown
//  ----------------------------------------------------------------------------
//  A calm, live-styled markdown editor for the Record body. It keeps the source
//  text VERBATIM inside one contentEditable (white-space:pre-wrap), so the caret
//  offset always maps 1:1 to the source string — then re-renders on every input
//  into styled inline spans:
//      • # / ## / ###  → heading sizes (the hashes dim, the words grow)
//      • - / *         → bullet lines
//      • - [ ] / - [x] → a real, tappable checkbox (strikes when done)
//      • > quote        → a quiet left-bar quote
//      • **b** *i* `c`  → emphasis with the markers dimmed, the content styled
//  The "dim the syntax, style the content" school: you see the result instantly,
//  nothing is hidden or lost, and editing never fights the caret.
//
//  Typing "/" on a fresh word opens the COMMAND MENU — the formatting vocabulary
//  offered by name, so the writer never has to remember that a quote is "> " or
//  that a to-do is "- [ ] ". See the MD_CMDS block below.
//
//  LINKS are the one construct this file does not decide for itself: what counts
//  as a URL, what is safe to open and how a link is written are all md-link.ts's,
//  because two of the three are security rules and nothing in this file can be
//  unit-tested. Here we only render them and route the gestures — see mdInline,
//  the "link" command, ⌘K, and onClick.
//
//  LISTS are the second: md-list.ts owns what Tab, Shift-Tab and Enter do to a
//  bullet, a number or a to-do, for the same reason — those keys rewrite the
//  writer's text underneath their caret, and this file cannot be unit-tested.
//  Nesting is plain leading spaces in the source; what this file adds is the
//  indent you SEE (a margin, per level) and the marker that goes with the depth.
//
//  Exposes <LiveMarkdown> on window for md-compose.jsx.
// ════════════════════════════════════════════════════════════════════════════
const { useRef: useRefMD, useEffect: useEffectMD, useLayoutEffect: useLayoutEffectMD,
  useState: useStateMD } = React;
const { mdRgba: rgbaMD, mdIsLight: isLightMD, ink: inkMD,
  mdLinkHref: hrefMD, mdLinkScan: scanMD, mdLinkInsert: insertLinkMD,
  mdListParse: listParseMD, mdListIndent: listIndentMD, mdListOutdent: listOutdentMD,
  mdListContinue: listContinueMD, mdTodoShortcut: todoShortcutMD,
  MD_LIST_MAX: MD_LIST_MAX_MD,
  mdUnformat: unformatMD, mdBlockPrefix: blockPrefixMD,
  mdAutoCapitalize: autoCapitalizeMD,
  mdWikiBindings: wikiBindingsMD, mdWikiReconcile: wikiReconcileMD,
  mdWikiBind: wikiBindMD, mdWikiLinks: wikiLinksMD,
  mdTagTokens: tagTokensMD, mdTagName: tagNameMD,
  mdTagKey: tagKeyMD, mdTagFragment: tagFragmentMD,
  mdTagComplete: tagCompleteMD, mdTagAdd: tagAddMD,
  mdTagRemove: tagRemoveMD,
  mdStrongDirection: strongDirectionMD, mdDirectionLineAt: directionLineAtMD,
  mdInsertedText: insertedTextMD,
  mdLineDirections: lineDirectionsMD,
  mdReconcileLineDirections: reconcileLineDirectionsMD } = window;

/* ── caret helpers — offset is a plain character count into the editor's text,
   which equals the source offset because the DOM text is kept verbatim ── */
function mdCaretOffset(root) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  const range = sel.getRangeAt(0);
  if (!root.contains(range.endContainer)) return null;
  const pre = range.cloneRange();
  pre.selectNodeContents(root);
  pre.setEnd(range.endContainer, range.endOffset);
  return pre.toString().length;
}
/* The newline characters live in the DOM but are hidden (see mdSrcToHtml), and a
   hidden node is not a place a caret can visibly sit — so any offset that resolves
   INTO one has to be redirected to the equivalent visible position.

   Which position depends on where in the newline it landed, and the two cases are
   not the same line:
     pos 0   — the caret is BEFORE the newline, i.e. at the end of this line's own
               content. For a blank line that is the line's only caret position, so
               it stays in THIS block.
     pos 1   — the caret is AFTER the newline, i.e. the first position of the NEXT
               line, so it moves to the next block.
   Both aim at a BLOCK rather than at the next text node, because a blank line has
   no text node at all — just a <br> — and hunting for the next text node would sail
   straight past it and drop the caret a whole line down. Aiming at the block puts
   the caret before the <br>, which is exactly where a blank line's caret belongs. */
function mdIsHiddenNl(node) {
  const p = node && node.parentElement;
  return !!(p && p.classList && p.classList.contains('md-nl'));
}
/* The newline is not the only text with nowhere for a caret to stand. Every block
   MARKER is in the DOM verbatim and drawn at zero width — the bullet's "- ", the
   number's "3. ", the to-do's "- [ ] ", the nesting pad — because the glyph you
   see is CSS and the text is only there to keep the offsets honest. A caret
   offset at the tail of one of those is the FIRST position of the line's content,
   which is a real place to type; the box it resolves into is not.
   Leave it there and the engine moves it somewhere it considers visible, and
   measured, that is the block boundary — one whole line from where the writer is
   working. It bit hardest on an EMPTY item, whose content is nothing but markers:
   Enter, Tab, then a letter left the letter in the right line and the caret in
   the next one, so the following Enter split a line nobody was standing on.
   The fix is to express the same character position as somewhere a caret can
   live: aim at the BLOCK, just past the markers. The checkbox goes in that list
   too — it is contenteditable="false", so it is no more standable-in than they
   are. */
const MD_ZW_MARK = /(?:^|\s)md-(?:bullet-src|num-src|todo-src|pad-src|check|embed-src)(?:\s|$)/;
function mdIsZeroWidth(node) {
  return !!(node && node.nodeType === 1 && typeof node.className === 'string' &&
    MD_ZW_MARK.test(node.className));
}
function mdSetCaret(root, offset) {
  if (offset == null) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  let node, remaining = offset, found = null;
  while ((node = walker.nextNode())) {
    const len = node.textContent.length;
    if (remaining <= len) { found = { node, pos: remaining }; break; }
    remaining -= len;
  }
  const sel = window.getSelection();
  const range = document.createRange();
  /* Nothing matched: the offset is past the end, or — the case that matters — the
     document holds no text at all, which is what a brand-new record is. Land inside
     the LAST BLOCK, never in the editor root. A caret with no text node beside it
     takes its height from whatever element it sits in, and the root carries body
     size while the block carries the line's own (21px on the title). Sitting in
     the root is why a new record's caret was a short body-height bar above the tall
     letters it was about to type.
     A block whose only child is the <br> sentinel is a line with no content: its
     offset 0 is ON that line and offset 1 is the phantom line after the break, so
     collapsing to the end there would drop the caret a line. */
  const atEnd = () => {
    const blk = root.lastElementChild;
    if (!blk) { range.selectNodeContents(root); range.collapse(false); return; }
    if (blk.firstChild && blk.firstChild.nodeName === 'BR') range.setStart(blk, 0);
    else { range.selectNodeContents(blk); range.collapse(false); }
  };
  if (found && mdIsHiddenNl(found.node)) {
    const blk = found.node.parentElement.closest('.md-ln');
    const target = found.pos === 0 ? blk : blk && blk.nextElementSibling;
    if (target) range.setStart(target, 0); else atEnd();
  } else if (found && mdIsZeroWidth(found.node.parentElement)) {
    // at the head of a marker → before it; at its tail → past every marker that
    // follows, which is where the line's own words begin
    const mark = found.node.parentElement;
    if (found.pos === 0) range.setStartBefore(mark);
    else {
      let ref = mark;
      while (mdIsZeroWidth(ref.nextSibling)) ref = ref.nextSibling;
      /* WebKit can accept text at the DOM seam after a non-editable checkbox
         without painting it until the editor remounts. The todo text span is a
         real editable landing place, including while it is empty. */
      const todoText = ref.nextSibling && ref.nextSibling.nodeType === 1
        && ref.nextSibling.classList.contains('md-todo-txt') ? ref.nextSibling : null;
      if (todoText) range.setStart(todoText, 0);
      else range.setStartAfter(ref);
    }
  } else if (found) range.setStart(found.node, found.pos);
  else atEnd();
  range.collapse(true);
  sel.removeAllRanges();
  sel.addRange(range);
}

const mdEsc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
/* An ATTRIBUTE value, which mdEsc alone does not make safe: it leaves the quote
   character alone, and data-href is a quoted attribute. A target containing a
   `"` would close it and everything after would be parsed as markup. */
const mdAttr = (s) => mdEsc(s).replace(/"/g, '&quot;');

/* One link, however it was written. `data-href` carries the RESOLVED target —
   the click handler never re-reads the writer's text — and its absence is what
   marks a link the editor will render but refuse to follow (see mdLinkHref: a
   `[tap me](javascript:…)` is valid markdown and this editor opens what it is
   handed). The link still gets its colour and its underline either way, because
   the record's job is to show what the source says, not to hide it. */
function mdLinkSpan(label, target, accent) {
  const href = hrefMD(target);
  return `<span class="md-link"${href ? ` data-href="${mdAttr(href)}"` : ''} style="color:${accent}">${mdEsc(label)}</span>`;
}

/* ── inline emphasis: **bold**, *italic*, `code`, [link](url) — markers dimmed,
   plus bare URLs lit up where they stand ── */
function mdInline(text, accent, baseOffset = 0, wikiByStart = null) {
  const mk = (s) => `<span class="md-mk">${mdEsc(s)}</span>`;
  /* A run of text that markdown has already finished with. Autolinking happens
     HERE and only here, and that placement is what makes it safe by
     construction: a URL inside a code span, or in a link's own target, was
     consumed by the regex below before this run was ever cut, so it can never
     reach the scanner and be linked a second time. */
  const tagged = (s) => {
    if (!tagTokensMD) return mdEsc(s);
    let out = '', at = 0;
    for (const tag of tagTokensMD(s)) {
      out += mdEsc(s.slice(at, tag.start));
      out += `<span class="md-tag" data-tag-key="${mdAttr(tag.key)}" style="color:${accent}">${mdEsc(tag.raw)}</span>`;
      at = tag.end;
    }
    return out + mdEsc(s.slice(at));
  };
  const plain = (s) => {
    let o = '', j = 0;
    for (const r of scanMD(s)) {
      o += tagged(s.slice(j, r.start)) + mdLinkSpan(s.slice(r.start, r.end), r.href, accent);
      j = r.end;
    }
    return o + tagged(s.slice(j));
  };
  let out = '', i = 0;
  /* The last alternative is markdown's own AUTOLINK, `<https://…>`, and it earns
     its place by being the only way to write an address that stays an address:
     a link alone on a line is a card now (see md-embed.ts), and a writer who
     wants the bare URL there needs something to say so. The angle brackets are
     dimmed like every other marker in this file, so what you read is the link
     and what you edit is what you typed. */
  const re = /(\[\[([^\[\]\n]{1,120})\]\])|(\*\*([^*]+)\*\*)|(\*([^*\n]+)\*)|(`([^`\n]+)`)|(\[([^\]\n]+)\]\(([^)\n]+)\))|(<((?:https?:\/\/|www\.)[^\s<>]+)>)/g;
  let m;
  while ((m = re.exec(text))) {
    out += plain(text.slice(i, m.index));
    if (m[1]) {
      const start = baseOffset + m.index;
      const target = wikiByStart && wikiByStart.get(start);
      out += mk('[[') + `<span class="md-wiki${target ? '' : ' md-wiki-unresolved'}" data-wiki-start="${start}" style="color:${target ? accent : inkMD(0.6)}">${mdEsc((m[2] || '').trim())}</span>` + mk(']]');
    }
    else if (m[3]) out += mk('**') + `<span class="md-b">${mdEsc(m[4])}</span>` + mk('**');
    else if (m[5]) out += mk('*') + `<span class="md-i">${mdEsc(m[6])}</span>` + mk('*');
    else if (m[7]) out += mk('`') + `<span class="md-code">${mdEsc(m[8])}</span>` + mk('`');
    else if (m[9]) out += mk('[') + mdLinkSpan(m[10], m[11], accent) + mk('](' + m[11] + ')');
    else if (m[12]) out += mk('<') + mdLinkSpan(m[13], m[13], accent) + mk('>');
    i = m.index + m[0].length;
  }
  out += plain(text.slice(i));
  return out || '';
}

/* ── the block model ────────────────────────────────────────────────────────
   Every source line renders as its OWN block, and the newline that follows it
   rides at the end of that block inside a zero-width <span class="md-nl">.

   Why not leave the \n as plain text in one big pre-wrap box, the way this file
   used to? Because an INLINE span cannot take a block margin, and block margin is
   the whole of typographic rhythm: air above a heading, a paragraph gap, a tight
   run of list items, a hanging indent for a bullet that wraps. In one pre-wrap box
   every line was locked to the same 24px slab, headings sat exactly as far from
   their own body text as two prose lines sat from each other, and the only
   spacing tool the writer had was typing a blank line.

   The newline has to STAY in the DOM as real text, because the caret model in this
   file rests on el.textContent being byte-identical to the source. So it is hidden
   in CSS, not removed — display:none, which renders nothing while textContent and
   Range.toString() (both DOM operations, both blind to CSS) still see every
   character. See the .md-nl note in the sheet for why it must be display:none and
   not collapsed whitespace: collapsed whitespace at a line end is whitespace the
   editing engine will delete out from under you as you type.

   It rides INSIDE its line's block, never between two blocks. A bare text node
   sitting between blocks gets wrapped in an anonymous block box, and an anonymous
   block box takes its strut from the CONTAINING block's font and line-height —
   25.6px of it — which would have quietly reinstated the very slab this removes. */

/* A line's kind and — for list items only — how deep it is nested. Everything
   else is level 0: Tab indents lists and nothing else, so a paragraph that
   happens to start with spaces is a paragraph the writer typed spaces into and
   they stay where they were put. md-list.ts answers the list half, so the
   grammar the renderer draws and the grammar the keys edit cannot drift. */
function mdLineKind(line, i, firstLineTitle, blockOf) {
  if (firstLineTitle && i === 0) return { kind: 'title', level: 0 };
  if (line === '') return { kind: 'blank', level: 0 };
  /* A line the host claims as a BLOCK — a video, a picture, a document. It is
     asked before every other rule, because a line that is nothing but a link is
     no longer a line of prose that happens to contain one, and none of the
     inline rules below have anything to say about it.
     What the block IS, this file never learns: it is handed a box to reserve
     and a key to draw by, and md-embed.ts holds the grammar. That is the same
     division md-link.ts and md-list.ts already have with this file. */
  if (blockOf) {
    const box = blockOf(line, i);
    if (box) return { kind: 'embed', level: 0, box };
  }
  const item = listParseMD(line);           // li / ol / todo, at its level
  if (item) return { kind: item.kind, level: item.level };
  let m;
  if ((m = /^(#{1,3}) /.exec(line))) return { kind: 'h' + m[1].length, level: 0 };
  if (/^> /.test(line)) return { kind: 'quote', level: 0 };
  return { kind: 'p', level: 0 };
}

/* Space is applied as margin-TOP only, chosen here in JS where both the current
   and the previous line's kind are known. Two reasons it is not left to CSS:
   sibling selectors would have to hop the hidden newline, and margin-bottom plus
   margin-top across blocks that cannot collapse would add instead of collapse.
   One margin, computed from context, is deterministic.

   The rule that matters most is `prev is a heading → xs`: a heading belongs to the
   text beneath it, so the gap below it must be far smaller than the gap above it.
   That is the difference between a document with sections and a document with
   evenly-spaced lines, and it is what the old single-slab model could not say. */
const MD_GAP = { none: 'md-gap-0', xs: 'md-gap-xs', sm: 'md-gap-sm', md: 'md-gap-md',
  lg: 'md-gap-lg', xl: 'md-gap-xl', title: 'md-gap-title' };
function mdGapFor(kind, prev) {
  if (prev == null) return MD_GAP.none;                      // first line of the record
  /* The one interval in a record that carries the whole hierarchy, and the one
     that cannot borrow a paragraph's. Every gap on the scale above is an em of
     the block it sits ON — the body's em — and the space under a title is set
     by the TITLE, which is 21px of display type whatever the body is doing. Its
     own class, so it is read against the title's metrics rather than prose. */
  if (prev === 'title') return MD_GAP.title;
  if (prev === 'h1' || prev === 'h2' || prev === 'h3') return MD_GAP.xs;
  // A blank line the writer typed already IS the gap — never stack a margin on it,
  // in either direction, or one Enter-Enter opens a chasm.
  if (kind === 'blank' || prev === 'blank') return MD_GAP.none;
  /* An embed is a picture in a page: it needs air on both sides of it, and the
     same amount, or the writing below it reads as a caption it does not have.
     This is also what closes the dead band under a record's title — the title
     used to be followed by a line of URL text and then a player drawn somewhere
     beneath the editor entirely. */
  if (kind === 'embed' || prev === 'embed') return MD_GAP.lg;
  if (kind === 'h1') return MD_GAP.xl;
  if (kind === 'h2' || kind === 'h3') return MD_GAP.lg;
  const listish = (k) => k === 'li' || k === 'ol' || k === 'todo';
  if (listish(kind)) return listish(prev) ? MD_GAP.xs : MD_GAP.md;
  if (kind === 'quote') return prev === 'quote' ? MD_GAP.xs : MD_GAP.md;
  return MD_GAP.sm;                                          // prose after prose
}

/* The line's INNER html. Size, weight and leading now live on the block, so the
   heading text is no longer wrapped in its own sized span — that wrapper is what
   put the dimmed "##" on a different baseline from the words beside it (measured:
   8px adrift), because two inline spans of different font-size in one line box
   each get their own half-leading. */
function mdRenderInner(line, kind, idx, accent, lineStart, wikiByStart) {
  let m;
  if (kind === 'h1' || kind === 'h2' || kind === 'h3') {
    m = /^(#{1,3}) (.*)$/.exec(line);
    if (m) return `<span class="md-mk md-mk-h">${m[1]} </span>${mdInline(m[2], accent, lineStart + m[1].length + 1, wikiByStart)}`;
  }
  if (kind === 'li' || kind === 'ol' || kind === 'todo') {
    const item = listParseMD(line);
    if (item) {
      /* The nesting spaces are text like every other marker: they stay in the DOM
         so textContent is still the source verbatim, and they are given no width,
         because the indent you SEE is a margin on the block (see .md-lv-* in the
         sheet). Rendering them as real spaces would indent the line twice and put
         the caret in the middle of the padding. */
      const pad = item.pad ? `<span class="md-pad-src">${mdEsc(item.pad)}</span>` : '';
      if (kind === 'todo') {
        const checked = /\[[xX]\]/.test(item.marker);
        const box = `<span class="md-check ${checked ? 'on' : ''}" data-todo data-line="${idx}" contenteditable="false" role="checkbox" aria-label="${checked ? 'Mark to-do incomplete' : 'Mark to-do complete'}" aria-checked="${checked}" style="--ac:${accent}"></span>`;
        // keep the source verbatim (the "- [ ] " text lives in a zero-width marker span so offsets still map)
        return `${pad}<span class="md-mk md-todo-src">${mdEsc(item.marker)}</span>${box}` +
          `<span class="md-todo-txt ${checked ? 'done' : ''}">${item.content
            ? mdInline(item.content, accent, lineStart + item.pad.length + item.marker.length, wikiByStart)
            : '<br>'}</span>`;
      }
      /* The bullet glyph is drawn by CSS (.md-ln-li::before), NOT emitted as text.
         It used to be a real "•" text node followed by a real space, which put two
         characters into textContent that were not in the source — so from the first
         bullet onwards every caret offset in the record was off by two per bullet, and
         the caret jumped when you edited near one. Only the literal "- " belongs in
         the DOM as text, and it is already there, zero-width, in md-bullet-src.

         Same contract for the number: the digits the writer typed stay in the DOM
         as zero-width text so offsets keep mapping, and the number you SEE is drawn
         by CSS — a counter, not this text. That is what renumbers the list, and at
         a nested level it is also what turns 1,2,3 into a,b,c. Delete the second of
         five items and the source still reads 1,3,4,5 while the record reads 1,2,3,4;
         the source digits are never visible, so the only number that can be wrong
         is one nobody can see. Notion renumbers the same way, and a counter does it
         without rewriting the writer's text under them on every keystroke. */
      const cls = kind === 'ol' ? 'md-num-src' : 'md-bullet-src';
      return `${pad}<span class="md-mk ${cls}">${mdEsc(item.marker)}</span>${mdInline(item.content, accent, lineStart + item.pad.length + item.marker.length, wikiByStart)}`;
    }
  }
  if (kind === 'quote') {
    m = /^> (.*)$/.exec(line);
    if (m) return `<span class="md-mk">&gt; </span><span class="md-quote-txt">${mdInline(m[1], accent, lineStart + 2, wikiByStart)}</span>`;
  }
  // title mode: line 0 IS the record's name (Apple-Notes style) — no marker needed.
  // Inline emphasis still works; block markers stay literal.
  return mdInline(line, accent, lineStart, wikiByStart);
}

/* Which ordered-list counters this line brings to an end, as one class naming the
   shallowest level it resets — everything from there down goes with it.

   It has to be ONE class: counter-reset is a property like any other, so two rules
   each setting it on the same element do not merge, the last one simply wins. And
   it has to be computed here rather than expressed as a CSS sibling selector,
   because the document is a flat run of blocks with no nesting to select through.

   The rule: a line ends the numbering at its own level and at every level below,
   EXCEPT that an ordered item is what continues its own level, so it only ends the
   levels beneath it. A nested item never touches its parent's count — which is
   what lets a sub-list sit between "1." and "2." without restarting the outer one. */
function mdResetFrom(kind, level) {
  return Math.min(kind === 'ol' ? level + 1 : level, MD_LIST_MAX_MD + 1);
}

/* ── the reserved slot ──────────────────────────────────────────────────────
   An embed's line renders as an EMPTY BOX OF THE RIGHT SIZE and nothing else.
   The player itself is drawn into that box from outside the contentEditable —
   see the overlay in LiveMarkdown — because this file rewrites its own innerHTML
   on every keystroke and an <iframe> living in that subtree would be torn down
   and rebuilt per character. A video that restarts when you type is worse than
   no video at all.

   The line's own text still rides inside the box, at zero size, exactly like
   every other marker in this file (see mdIsZeroWidth). That is what keeps the
   caret model honest: el.textContent is still the source verbatim, so a
   character count into the DOM is still a character count into the writing, and
   the URL nobody wants to look at is still there to be selected, copied and
   deleted like the writing it is. */
function mdSlotStyle(box) {
  const st = [];
  if (box.width > 0) st.push(`width:${Math.round(box.width)}px`);
  if (box.maxWidth > 0) st.push(`max-width:${Math.round(box.maxWidth)}px`);
  if (box.height > 0) st.push(`height:${Math.round(box.height)}px`);
  else if (box.ratio > 0) st.push(`aspect-ratio:${box.ratio}`);
  return st.join(';');
}

function mdSrcToHtml(src, accent, firstLineTitle, blockOf, wikiBindings = [], lineDirections = []) {
  const lines = src.split('\n');
  let prev = null;
  let lineStart = 0;
  const wikiByStart = new Map((wikiBindings || []).map((row) => [row.start, row.target]));
  return lines.map((ln, i) => {
    const info = mdLineKind(ln, i, firstLineTitle, blockOf);
    const kind = info.kind;
    const depth = ` md-lv-${info.level} md-rst-${mdResetFrom(kind, info.level)}`;
    const gap = mdGapFor(kind, prev);
    const direction = lineDirections[i] === 'rtl' ? 'rtl' : 'ltr';
    prev = kind;
    if (kind === 'embed') {
      const nl = i < lines.length - 1 ? '<span class="md-nl">\n</span>' : '';
      const html = `<span class="md-ln md-ln-embed ${gap}${depth}" dir="${direction}" data-embed="${i}"` +
        ` data-key="${mdAttr(info.box.key)}" style="${mdSlotStyle(info.box)}">` +
        `<span class="md-embed-src">${mdEsc(ln)}</span>${nl}</span>`;
      lineStart += ln.length + 1;
      return html;
    }
    // An empty block has no line box, so the caret has nowhere to land on a blank
    // line and Enter on the last line appears to do nothing. A <br> gives the block
    // a real line box while contributing NOTHING to textContent, so the offset
    // mapping is untouched. This also covers a source that ends in \n, which used
    // to need its own trailing sentinel.
    // The test is the LINE, not the kind: an empty first line is a `title`, not a
    // `blank`, and it needs the line box just as much — that is the untouched
    // title of every new record, and without a box its caret fell back to the
    // editor's body size instead of standing at the title's own 21px.
    const inner = kind === 'blank' || ln === '' ? '<br>' : mdRenderInner(ln, kind, i, accent, lineStart, wikiByStart);
    const nl = i < lines.length - 1 ? '<span class="md-nl">\n</span>' : '';
    const html = `<span class="md-ln md-ln-${kind} ${gap}${depth}" dir="${direction}">${inner}${nl}</span>`;
    lineStart += ln.length + 1;
    return html;
  }).join('');
}

/* ── the "/" command menu ───────────────────────────────────────────────────
   Markdown's syntax is only obvious to people who already know it. "- [ ] " for a
   to-do is not something a writer guesses, and the toolbar has no room to teach it
   (see md-compose: the When label plus Save already overran the bar). So the
   vocabulary is offered where the writing happens: type "/" and ask for it by name.

   Every command is a SOURCE EDIT, never a formatting flag — picking "Heading 2"
   writes "## " at the head of the line, exactly what the writer would have typed.
   That is what keeps one model of the document: the text is still the whole truth,
   the menu is only a faster way to type it. It also means the menu teaches — each
   row shows the syntax it writes, so the second time you reach for a quote you can
   just type ">" and skip the menu entirely.

   `syntax` is that hint. `kw` is the search vocabulary, deliberately wider than the
   label: "checkbox", "task" and "tick" all have to find the to-do, because the name
   a writer reaches for is not the name we happened to print. */
const MD_CMDS = [
{ id: 'h1', label: 'Heading 1', syntax: '#', kw: 'h1 heading1 title big large section', block: '# ' },
{ id: 'h2', label: 'Heading 2', syntax: '##', kw: 'h2 heading2 subtitle subhead medium', block: '## ' },
{ id: 'h3', label: 'Heading 3', syntax: '###', kw: 'h3 heading3 small minor', block: '### ' },
{ id: 'li', label: 'Bulleted list', syntax: '-', kw: 'bullet list unordered ul point item dash', block: '- ' },
{ id: 'ol', label: 'Numbered list', syntax: '1.', kw: 'numbered number ordered ol steps sequence list', block: '1. ' },
{ id: 'todo', label: 'To-do', syntax: '[]', kw: 'todo task checkbox check tick box done', block: '- [ ] ' },
{ id: 'quote', label: 'Quote', syntax: '>', kw: 'quote blockquote cite quotation', block: '> ' },
{ id: 'bold', label: 'Bold', syntax: '**', kw: 'bold strong heavy b', wrap: '**' },
{ id: 'italic', label: 'Italic', syntax: '*', kw: 'italic emphasis oblique i', wrap: '*' },
{ id: 'code', label: 'Code', syntax: '`', kw: 'code mono monospace inline tt', wrap: '`' },
/* A third kind of applier beside `block` and `wrap`, because a link is the one
   construct with TWO slots to fill and therefore an opinion about which of them
   the caret lands in. The rest of the contract is unchanged: it writes plain
   markdown into the source and the writer can type the same thing by hand.
   NOTE this now leads the ranking for "/l" and "/li" — an exact label prefix
   outranks a keyword prefix, so it comes in ahead of "Bulleted list", which is
   the existing rule in mdCmdMatch doing exactly what it says. "/lis" still
   reaches the list alone. */
{ id: 'link', label: 'Link', syntax: '[]()', kw: 'link url href web address hyperlink anchor http site', link: true },
{ id: 'database', label: 'Database', syntax: 'table', kw: 'database table rows columns data source collection board list', database: true },
/* The fourth kind of applier. An embed has no syntax worth teaching — the whole
   grammar is "a link on a line of its own" — so what this row offers is not a
   spelling but a PLACE TO PUT THE ADDRESS. Pick it and you are asked for the
   link; give one and the line becomes what it points at. That is the same
   gesture Notion's /embed makes, and it is the only command here that needs
   something from the writer before it can do anything. */
{ id: 'embed', label: 'Embed', syntax: '↵', kw: 'embed video youtube vimeo image picture photo gif media player bookmark card iframe attach', embed: true }];

const MD_LINK_CMD = MD_CMDS.filter((c) => c.id === 'link')[0];


/* Ranked, not merely filtered. A label prefix beats a keyword prefix beats a
   substring, so "h" leads with the three headings rather than with "Body text"
   (which contains an h in "Heading"… it doesn't, but "quote" does contain a t and
   substring-only matching produces exactly that kind of nonsense ordering). */
function mdCmdMatch(query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return MD_CMDS;
  const out = [];
  MD_CMDS.forEach((c, i) => {
    const label = c.label.toLowerCase();
    const hay = label + ' ' + c.kw;
    let rank = -1;
    if (label.startsWith(q)) rank = 0;else
    if (hay.split(' ').some((w) => w.startsWith(q))) rank = 1;else
    // A bare substring is only evidence once the query is more than one letter.
    // "h" alone lands inside dash, paragraph and emphasis, which put eight of the
    // ten commands on screen for the query that most obviously means "heading".
    if (q.length > 1 && hay.indexOf(q) >= 0) rank = 2;
    if (rank >= 0) out.push({ c, rank, i });
  });
  out.sort((a, b) => a.rank - b.rank || a.i - b.i);
  return out.map((x) => x.c);
}

/* Picking a format REPLACES the line's existing one rather than stacking on it —
   "## Hi" asked to become a bullet is "- Hi", never "- ## Hi". Which prefixes
   count as a format is mdBlockPrefix's, in md-unformat.ts: the menu writes them,
   Backspace takes them away, and mdLineKind draws them, so the grammar lives in
   one place where all three read it. The leading whitespace comes back
   separately and is kept — a nested bullet asked to become a to-do is a nested
   to-do, not one that jumped back to the margin. */

/* Both appliers take the source plus the [s, e) span of the typed "/query" and
   return the new source with the caret offset to put back. The query goes first —
   it was never content, it was a request — and then the line is rewritten. */
function mdApplyBlock(src, s, e, prefix) {
  const cut = src.slice(0, s) + src.slice(e);
  const ls = cut.lastIndexOf('\n', s - 1) + 1;
  let le = cut.indexOf('\n', ls);if (le < 0) le = cut.length;
  const line = cut.slice(ls, le);
  const { pad, marker } = blockPrefixMD(line);
  const old = pad + marker;
  const head = pad + prefix;
  const next = head + line.slice(old.length);
  /* The caret keeps its place in the WORDS, not its offset in the string: the
     writer typed "/quote" in the middle of a sentence and expects to carry on
     from there, so the caret shifts by exactly the change in prefix length. */
  const caret = Math.max(ls + head.length, Math.min(s - old.length + head.length, ls + next.length));
  return { src: cut.slice(0, ls) + next + cut.slice(le), caret };
}
function mdApplyWrap(src, s, e, w) {
  const cut = src.slice(0, s) + src.slice(e);
  // Opened and closed in one go, caret in the middle: you asked to write bold, so
  // the next thing you type is bold. Leaving the closer to the writer is how you
  // get a record full of half-open emphasis.
  return { src: cut.slice(0, s) + w + w + cut.slice(s), caret: s + w.length };
}
/* Same shape again — the query goes first, then the edit lands where it was.
   The edit itself is md-link.ts's, so the menu and ⌘K cannot drift apart. */
function mdApplyLink(src, s, e) {
  return insertLinkMD(src.slice(0, s) + src.slice(e), s, s);
}

/* An address, put where it can become a card: ON A LINE OF ITS OWN. That is the
   one rule md-embed.ts has, so this is the one thing this function guarantees.
   A line with writing already on it is not disturbed — the embed goes on the
   next line down, which is what somebody typing mid-sentence meant, rather than
   a paragraph silently turned into a video. */
function mdApplyEmbed(src, s, e, url) {
  const cut = src.slice(0, s) + src.slice(e);
  const ls = cut.lastIndexOf('\n', s - 1) + 1;
  let le = cut.indexOf('\n', ls); if (le < 0) le = cut.length;
  const line = cut.slice(ls, le);
  if (!line.trim()) {
    return { src: cut.slice(0, ls) + url + cut.slice(le), caret: ls + url.length };
  }
  const head = cut.slice(0, le) + '\n' + url;
  return { src: head + cut.slice(le), caret: head.length };
}

/* The selection as SOURCE offsets. mdCaretOffset answers for one end only, and
   ⌘K needs both: a selection is what turns "insert a link" into "make THIS the
   label". The same identity holds as everywhere else in this file — the DOM text
   is verbatim, so a character count into it is a character count into the
   source, hidden newlines included (Range.toString() ignores CSS). */
function mdSelRange(root) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  const r = sel.getRangeAt(0);
  if (!root.contains(r.startContainer) || !root.contains(r.endContainer)) return null;
  const pre = r.cloneRange();
  pre.selectNodeContents(root);
  pre.setEnd(r.startContainer, r.startOffset);
  const start = pre.toString().length;
  return { start, end: start + r.toString().length };
}

/* Open a rendered link. Re-checked through mdLinkHref on the way out even
   though we wrote the attribute ourselves: the check is a regex and a parse,
   and the thing it prevents is arbitrary script running in the page that holds
   someone's journal. There is no version of that trade worth the microsecond. */
function mdOpenLink(node) {
  const href = node && node.getAttribute && hrefMD(node.getAttribute('data-href'));
  if (!href) return false;
  window.open(href, '_blank', 'noopener,noreferrer');
  return true;
}

/* Escape closes an open caret panel WITHOUT closing compose. That used to need
   a module-load window-capture listener racing to run before every other
   Escape door in the app. There is one door now: while a caret panel is open
   the editor holds the top layer of the dismiss arbiter (md-dismiss.jsx), and
   the stack — not registration order — says the innermost surface goes first.
   The layer is registered in the editor component below. */

/* The caret's rectangle, in client coordinates. A collapsed range inside an empty
   element (a blank line, whose only content is a <br>) has no client rects at all,
   so fall back to the line's own block — which is where a blank line's caret is. */
function mdCaretRect(root) {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount) return null;
  const r = sel.getRangeAt(0);
  if (!root.contains(r.startContainer)) return null;
  const rects = r.getClientRects();
  let box = rects.length ? rects[rects.length - 1] : null;
  if (!box || !box.height) {
    const n = r.startContainer;
    const el = n.nodeType === 1 ? n : n.parentElement;
    const blk = el && el.closest && el.closest('.md-ln');
    box = (blk || root).getBoundingClientRect();
  }
  return { left: box.left, top: box.top, bottom: box.bottom };
}

/* An alpha of the accent. `accent` arrives here already resolved to a full
   "rgba(…)" string (md-compose passes mdRgba(accent, 1)), not the "#RRGGBB" that
   mdRgba itself expects — feed one to the other and you get rgba(NaN,NaN,NaN).
   So accept either and re-alpha whichever turned up. */
function mdAccentA(c, a) {
  if (!c) return `rgba(127,127,127,${a})`;
  if (c.charAt(0) === '#') return rgbaMD(c, a);
  const m = /rgba?\(([^)]+)\)/.exec(c);
  if (!m) return c;
  const p = m[1].split(',');
  return `rgba(${p[0].trim()},${p[1].trim()},${p[2].trim()},${a})`;
}

function mdCmdGlyph(id, c) {
  const T = (s, st) => <span style={{ fontSize: 11.5, fontWeight: 800, letterSpacing: -0.3, color: c, lineHeight: 1, ...st }}>{s}</span>;
  const svg = (kids) => <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">{kids}</svg>;
  if (id === 'h1') return T('H1');
  if (id === 'h2') return T('H2');
  if (id === 'h3') return T('H3');
  if (id === 'bold') return T('B', { fontSize: 13.5 });
  if (id === 'italic') return T('I', { fontSize: 13.5, fontStyle: 'italic' });
  if (id === 'code') return T('‹›', { fontSize: 14, fontWeight: 700 });
  if (id === 'li') return svg([
  <circle key="a" cx="5" cy="7.5" r="1.7" fill={c} />,
  <circle key="b" cx="5" cy="16.5" r="1.7" fill={c} />,
  <path key="c" d="M10 7.5h9M10 16.5h9" stroke={c} strokeWidth="1.8" strokeLinecap="round" />]);

  if (id === 'ol') return svg([
  <path key="a" d="M4 5.5h1.6V11M3.6 16.2h2.8L3.6 20h2.9" stroke={c} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />,
  <path key="b" d="M10 8h9M10 18h9" stroke={c} strokeWidth="1.8" strokeLinecap="round" />]);

  if (id === 'todo') return svg([
  <rect key="a" x="3.4" y="3.4" width="17.2" height="17.2" rx="4.6" stroke={c} strokeWidth="1.8" />,
  <path key="b" d="M7.9 12.1l2.9 2.9 5.4-6" stroke={c} strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />]);

  if (id === 'quote') return svg([
  <path key="a" d="M4.6 5.5v13" stroke={c} strokeWidth="2" strokeLinecap="round" />,
  <path key="b" d="M9.5 8.5h10M9.5 13h10M9.5 17.5h6" stroke={c} strokeWidth="1.7" strokeLinecap="round" />]);

  if (id === 'database') return svg([
  <rect key="a" x="3" y="4" width="18" height="16" rx="2.7" stroke={c} strokeWidth="1.7" />,
  <path key="b" d="M3 9h18M9 9v11" stroke={c} strokeWidth="1.7" />]);

  // a frame with a play triangle in it: the thing itself, shown here
  if (id === 'embed') return svg([
  <rect key="a" x="3" y="4.6" width="18" height="14.8" rx="3.4" stroke={c} strokeWidth="1.7" />,
  <path key="b" d="M10.3 9.4l4.8 2.6-4.8 2.6z" fill={c} />]);

  // two half-links leaning away from each other — the chain every editor draws
  if (id === 'link') return svg([
  <path key="a" d="M10.5 13.5a3.7 3.7 0 005.4.3l2.6-2.6a3.7 3.7 0 00-5.2-5.2l-1.5 1.5" stroke={c} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />,
  <path key="b" d="M13.5 10.5a3.7 3.7 0 00-5.4-.3l-2.6 2.6a3.7 3.7 0 005.2 5.2l1.5-1.5" stroke={c} strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />]);

  return null;
}

/* THE CARET LAYER, and it is one number rather than four.
   These panels hang off the caret, and while one is open it is the thing the
   writer is steering — nothing in this app legitimately covers it. Because they
   portal to <body> they stack against every HOST SURFACE rather than inside it,
   and at 120 they lost: the Projects window stands at 140 and the New project
   sheet at 200, so typing "/" in either drew the whole vocabulary behind an
   opaque card. The menu was there, correctly placed, and invisible. Above every
   surface in the app (the ceiling is the sheet's own property menus at 210), and
   the +2 keeps the record and tag panels over the command list as before. */
const MD_CARET_Z = 300;
/* The desk's live caret is a second KIND of caret, not a second layer. Board is
   loaded before this script but renders after all scripts have run, so publish
   the established ceiling here rather than copying a number that can drift. */
window.MD_CARET_Z = MD_CARET_Z;

/* ── the menu surface ──────────────────────────────────────────────────────
   Portalled to <body>, because its natural parent is the contentEditable whose
   innerHTML this file rewrites on every keystroke — anything React put in there
   would be destroyed on the next character typed. Fixed position, measured against
   visualViewport rather than the window, so a software keyboard cannot swallow it:
   on a phone mid-sentence the only room left is ABOVE the caret, and that is a
   decision the layout has to make from the space that actually exists. */
function MdCmdMenu({ anchor, items, active, light, accent, onPick, onHover }) {
  const box = useRefMD(null);
  /* Keyboard navigation has to be able to reach a row that is scrolled out of the
     panel — the list is capped in height, and on a phone the cap can be four rows. */
  useEffectMD(() => {
    const el = box.current;if (!el) return;
    const row = el.children[active + 1];  // +1: the "Format" header is child 0
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [active, items.length]);

  const vv = window.visualViewport;
  const vTop = vv ? vv.offsetTop : 0;
  const vLeft = vv ? vv.offsetLeft : 0;
  const vW = vv ? vv.width : window.innerWidth;
  const vH = vv ? vv.height : window.innerHeight;
  const M = 10;                                    // breathing room at the edges
  const width = Math.min(272, vW - M * 2);
  /* The menu hangs from the caret on the side the writing came FROM, so it opens
     into the space the line has not used yet: left edge at the caret in LTR, right
     edge at the caret in RTL. Anchored the same way in both, an Arabic record would
     open the panel across the text just written. */
  const rtl = getComputedStyle(document.body).direction === 'rtl';
  const wanted = rtl ? anchor.left + 6 - width : anchor.left - 6;
  const left = Math.max(vLeft + M, Math.min(wanted, vLeft + vW - M - width));
  const below = vTop + vH - anchor.bottom - M - 6;
  const above = anchor.top - vTop - M - 6;
  const down = below >= 216 || below >= above;      // 216 ≈ five rows and the header
  // 424 is the whole vocabulary — ten 38px rows, the header and the padding. Where
  // the room exists the list does not scroll: a menu you have to scroll to reach
  // "Code" is a menu that hides half of what the editor can do.
  const maxHeight = Math.max(132, Math.min(424, down ? below : above));
  const place = down ?
  { top: Math.round(anchor.bottom + 6) } :
  { bottom: Math.round(window.innerHeight - anchor.top + 6) };
  const row = (bg) => ({ display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 38,
    padding: '0 10px', borderRadius: 10, border: 'none', background: bg, cursor: 'pointer',
    textAlign: 'left', transition: 'background .12s ease' });

  return ReactDOM.createPortal(
    <div id="md-cmd-menu" role="listbox" aria-label="Formatting commands" ref={box}
    /* Rows fire on pointerdown, but preventing THAT default is not enough: Chrome
       still runs the legacy mouse sequence, and a mousedown inside this panel puts
       the selection in the panel — measured, it landed the caret back at offset 0
       after a real click, having just been placed correctly. So the panel refuses
       the mouse's default outright. The caret belongs to the editor throughout. */
    onMouseDown={(e) => e.preventDefault()}
    style={{ position: 'fixed', left, ...place, width, zIndex: MD_CARET_Z,
      maxHeight, overflowY: 'auto', WebkitOverflowScrolling: 'touch',
      padding: 6, borderRadius: 16, animation: 'mdSlotIn .16s ease',
      background: light ? 'rgba(252,253,255,0.97)' : 'rgba(30,32,40,0.97)',
      border: `0.75px solid ${inkMD(light ? 0.08 : 0.16)}`,
      boxShadow: `0 14px 34px ${light ? 'rgba(30,42,72,0.22)' : 'rgba(0,0,0,0.55)'}`,
      backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
      // The caret stays in the editor and every row is driven from pointerdown,
      // so nothing in here is ever the selection's owner.
      userSelect: 'none', WebkitUserSelect: 'none' }}>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '2px 4px 6px' }}>
        <span style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: inkMD(0.5) }}>Format</span>
        <span style={{ fontSize: 10.5, fontWeight: 600, color: inkMD(0.35) }}>{items.length ? '↑↓ ⏎' : ''}</span>
      </div>
      {!items.length &&
      <div style={{ padding: '9px 10px 11px', fontSize: 13, color: inkMD(0.45) }}>No matching format</div>}
      {items.map((c, i) => {
        const on = i === active;
        const tint = on ? mdAccentA(accent, light ? 0.13 : 0.2) : 'transparent';
        return (
          <div key={c.id} id={'md-cmd-' + c.id} role="option" aria-selected={on}
          onPointerDown={(e) => {e.preventDefault();onPick(c);}}
          onPointerEnter={() => onHover(i)}
          style={row(tint)}>
            <span style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0,
              display: 'grid', placeItems: 'center',
              background: on ? mdAccentA(accent, light ? 0.18 : 0.26) : inkMD(light ? 0.06 : 0.1) }}>
              {mdCmdGlyph(c.id, on ? mdAccentA(accent, 1) : inkMD(0.62))}
            </span>
            <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 620, color: inkMD(0.9),
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.label}</span>
            {/* the syntax this row writes — the row is a lesson as well as a button.
                A one- or two-character chip is small enough that ink(0.4) on a near
                white panel (measured ~2.5:1) is simply not readable; 0.58 is the
                lowest that still reads as a hint rather than as a second label. */}
            {c.syntax &&
            <span dir="ltr" style={{ flexShrink: 0, fontFamily: 'ui-monospace,SFMono-Regular,Menlo,monospace',
              fontSize: 11, color: inkMD(0.58), background: inkMD(light ? 0.06 : 0.1),
              padding: '2px 5px', borderRadius: 5 }}>{c.syntax}</span>}
          </div>);

      })}
    </div>,
    document.body);

}

function MdWikiMenu({ anchor, items, active, light, accent, onPick, onHover }) {
  const box = useRefMD(null);
  useEffectMD(() => {
    const el = box.current;
    const row = el && el.querySelector('[aria-selected="true"]');
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [active, items.length]);
  const vv = window.visualViewport;
  const vTop = vv ? vv.offsetTop : 0, vLeft = vv ? vv.offsetLeft : 0;
  const vW = vv ? vv.width : window.innerWidth, vH = vv ? vv.height : window.innerHeight;
  const width = Math.min(310, vW - 20);
  const left = Math.max(vLeft + 10, Math.min(anchor.left - 6, vLeft + vW - width - 10));
  const below = vTop + vH - anchor.bottom - 16, above = anchor.top - vTop - 16;
  const down = below >= 190 || below >= above;
  const place = down ? { top: Math.round(anchor.bottom + 6) }
    : { bottom: Math.round(window.innerHeight - anchor.top + 6) };
  return ReactDOM.createPortal(
    <div id="md-wiki-menu" ref={box} role="listbox" aria-label="Link a record"
      onMouseDown={(e) => e.preventDefault()}
      style={{ position: 'fixed', left, ...place, width, zIndex: MD_CARET_Z + 2,
        maxHeight: Math.max(136, Math.min(342, down ? below : above)), overflowY: 'auto',
        padding: 6, borderRadius: 16, animation: 'mdSlotIn .16s ease',
        background: light ? 'rgba(252,253,255,0.97)' : 'rgba(30,32,40,0.97)',
        border: `0.75px solid ${inkMD(light ? 0.08 : 0.16)}`,
        boxShadow: `0 14px 34px ${light ? 'rgba(30,42,72,0.22)' : 'rgba(0,0,0,0.55)'}`,
        backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
        userSelect: 'none', WebkitUserSelect: 'none' }}>
      <div style={{ padding: '3px 6px 7px', fontSize: 10.5, fontWeight: 700,
        letterSpacing: 0.8, textTransform: 'uppercase', color: inkMD(0.48) }}>Link record</div>
      {!items.length && <div style={{ padding: '10px', fontSize: 13, color: inkMD(0.45) }}>Keep typing to create a record</div>}
      {items.map((row, i) => {
        const on = i === active;
        return <div key={row.key} id={`md-wiki-${i}`} role="option" aria-selected={on}
          onPointerDown={(e) => {e.preventDefault();onPick(row);}}
          onPointerEnter={() => onHover(i)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 44, padding: '5px 9px',
            borderRadius: 10, cursor: 'pointer',
            background: on ? mdAccentA(accent, light ? 0.13 : 0.2) : 'transparent' }}>
          <span aria-hidden="true" style={{ width: 25, height: 25, borderRadius: 8, flexShrink: 0,
            display: 'grid', placeItems: 'center', color: on ? accent : inkMD(0.56),
            background: on ? mdAccentA(accent, light ? 0.18 : 0.25) : inkMD(light ? 0.055 : 0.09),
            fontSize: row.create ? 19 : 12, fontWeight: 650 }}>{row.create ? '+' : (row.glyph || '•')}</span>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              fontSize: 14, fontWeight: 620, color: inkMD(0.9) }}>{row.create ? `Create “${row.title}”` : row.title}</span>
            {row.subtitle && <span style={{ display: 'block', marginTop: 1, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 11.5, color: inkMD(0.43) }}>{row.subtitle}</span>}
          </span>
        </div>;
      })}
    </div>, document.body);
}

function MdTagMenu({ anchor, items, active, light, accent, onPick, onHover }) {
  const box = useRefMD(null);
  useEffectMD(() => {
    const row = box.current && box.current.querySelector('[aria-selected="true"]');
    if (row && row.scrollIntoView) row.scrollIntoView({ block: 'nearest' });
  }, [active, items.length]);
  const vv = window.visualViewport;
  const vTop = vv ? vv.offsetTop : 0, vLeft = vv ? vv.offsetLeft : 0;
  const vW = vv ? vv.width : window.innerWidth, vH = vv ? vv.height : window.innerHeight;
  const width = Math.min(286, vW - 20);
  const left = Math.max(vLeft + 10, Math.min(anchor.left - 6, vLeft + vW - width - 10));
  const below = vTop + vH - anchor.bottom - 16, above = anchor.top - vTop - 16;
  const down = below >= 176 || below >= above;
  const place = down ? { top: Math.round(anchor.bottom + 6) }
    : { bottom: Math.round(window.innerHeight - anchor.top + 6) };
  return ReactDOM.createPortal(
    <div id="md-tag-menu" ref={box} role="listbox" aria-label="Add a tag"
      onMouseDown={(e) => e.preventDefault()}
      style={{ position: 'fixed', left, ...place, width, zIndex: MD_CARET_Z + 2,
        maxHeight: Math.max(132, Math.min(314, down ? below : above)), overflowY: 'auto',
        ...mdPanel(light), userSelect: 'none', WebkitUserSelect: 'none' }}>
      <div style={{ padding: '3px 6px 7px', fontSize: 10.5, fontWeight: 700,
        letterSpacing: 0.8, textTransform: 'uppercase', color: inkMD(0.48) }}>Tag</div>
      {!items.length && <div style={{ padding: '10px', fontSize: 13, color: inkMD(0.45) }}>
        Type a name to create a tag
      </div>}
      {items.map((row, i) => {
        const on = i === active;
        return <div key={row.key} id={`md-tag-${i}`} role="option" aria-selected={on}
          onPointerDown={(e) => {e.preventDefault();onPick(row);}}
          onPointerEnter={() => onHover(i)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 42, padding: '5px 9px',
            borderRadius: 10, cursor: 'pointer',
            background: on ? mdAccentA(accent, light ? 0.13 : 0.2) : 'transparent' }}>
          <span aria-hidden="true" style={{ width: 26, height: 26, borderRadius: 8, flexShrink: 0,
            display: 'grid', placeItems: 'center', color: on ? accent : inkMD(0.52),
            background: on ? mdAccentA(accent, light ? 0.18 : 0.25) : inkMD(light ? 0.055 : 0.09),
            fontSize: row.create ? 18 : 13, fontWeight: 680 }}>{row.create ? '+' : '#'}</span>
          <span style={{ minWidth: 0, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
            whiteSpace: 'nowrap', fontSize: 14, fontWeight: 620, color: inkMD(0.9) }}>
            {row.create ? `Create #${row.name}` : `#${row.name}`}
          </span>
          {!row.create && row.count > 0 && <span style={{ flexShrink: 0, fontSize: 11.5,
            fontVariantNumeric: 'tabular-nums', color: inkMD(0.4) }}>{row.count}</span>}
        </div>;
      })}
    </div>, document.body);
}

/* ── the two small surfaces ─────────────────────────────────────────────────
   Both hang from the caret, both are portalled to <body> for the same reason
   MdCmdMenu is (their natural parent is a contentEditable this file rewrites on
   every keystroke), and both share its panel so the editor speaks with one
   voice. What differs is only what they ask.

   The PANEL is factored out rather than copied — three surfaces with three
   slightly different shadows is how an app stops looking made by one hand. */
function mdPanel(light) {
  return {
    padding: 6, borderRadius: 16, animation: 'mdSlotIn .16s ease',
    background: light ? 'rgba(252,253,255,0.97)' : 'rgba(30,32,40,0.97)',
    border: `0.75px solid ${inkMD(light ? 0.08 : 0.16)}`,
    boxShadow: `0 14px 34px ${light ? 'rgba(30,42,72,0.22)' : 'rgba(0,0,0,0.55)'}`,
    backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)',
  };
}
/* Where a panel of this width goes, given the caret. Below when there is room,
   above when there is not, and never off either edge. visualViewport rather
   than the window, so a software keyboard cannot swallow it. */
function mdPlace(anchor, width, height) {
  const vv = window.visualViewport;
  const vTop = vv ? vv.offsetTop : 0, vLeft = vv ? vv.offsetLeft : 0;
  const vW = vv ? vv.width : window.innerWidth, vH = vv ? vv.height : window.innerHeight;
  const M = 10;
  const w = Math.min(width, vW - M * 2);
  const left = Math.max(vLeft + M, Math.min(anchor.left - 6, vLeft + vW - M - w));
  const below = vTop + vH - anchor.bottom - M - 6;
  const down = below >= height || below >= anchor.top - vTop;
  return { position: 'fixed', left, width: w, zIndex: MD_CARET_Z,
    ...(down ? { top: Math.round(anchor.bottom + 6) }
      : { bottom: Math.round(window.innerHeight - anchor.top + 6) }) };
}

/* ── "paste as" ─────────────────────────────────────────────────────────────
   An address just landed on a line of its own, so it is already a card — that
   is the rule, and the rule ran. This asks whether that is what you meant.

   IT OFFERS THE ALTERNATIVES, NOT THE DECISION. Notion pastes plain text and
   makes you pick; the default here is drawn already, because a link on its own
   line is nearly always something you want to see rather than an address you
   want to read. So the menu is a way OUT of the default, open for as long as it
   takes to notice it and gone the moment you carry on writing.

   WHAT IS ON OFFER IS NOT THIS FILE'S TO SAY. The rows come from `blockFormats`,
   which md-embed.jsx fills — the same list, in the same words, that the card's
   own ⋯ menu shows. An address that can be made a picture at the moment it
   lands but not a minute later would be two features; one list is how it stays
   one. This file keeps only what it has always kept: where the menu hangs, and
   that nothing it does takes the caret out of the writing.

   Escape closes it, typing closes it, and clicking anywhere closes it. */
function MdPasteAs({ anchor, light, accent, rows, onPick }) {
  return ReactDOM.createPortal(
    <div role="menu" aria-label="Paste as" onMouseDown={(e) => e.preventDefault()}
      style={{ ...mdPlace(anchor, 244, 44 + rows.length * 44), ...mdPanel(light), userSelect: 'none' }}>
      <div style={{ padding: '2px 4px 6px', fontSize: 10.5, fontWeight: 700,
        letterSpacing: 0.8, textTransform: 'uppercase', color: inkMD(0.5) }}>Paste as</div>
      {rows.map((r) => (
        <button key={r.id} role="menuitemradio" aria-checked={!!r.on}
          onPointerDown={(e) => { e.preventDefault(); onPick(r); }}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%',
            padding: '7px 10px 8px', borderRadius: 10, border: 'none', background: 'transparent',
            cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
          <span aria-hidden="true" style={{ width: 15, height: 15, borderRadius: 8, flexShrink: 0,
            display: 'grid', placeItems: 'center',
            border: `1.5px solid ${r.on ? mdAccentA(accent, 1) : inkMD(0.28)}` }}>
            {r.on && <span style={{ width: 7, height: 7, borderRadius: 4, background: mdAccentA(accent, 1) }} />}
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontSize: 13.5, fontWeight: 620, color: inkMD(0.9) }}>{r.label}</span>
            <span style={{ display: 'block', fontSize: 11.5, marginTop: 1, color: inkMD(0.42) }}>{r.hint}</span>
          </span>
        </button>))}
    </div>,
    document.body);
}

/* ── "/embed" asks for the address ──────────────────────────────────────────
   The one command that needs something before it can act. It takes the focus —
   it has to, it is an input — and gives it straight back: Enter writes the line
   and returns the caret to the writing, Escape returns it having done nothing.
   Nothing is written until there is a real address, so an empty Enter is a
   no-op rather than an empty line. */
function MdEmbedPrompt({ anchor, light, accent, onSubmit, onCancel }) {
  const ref = useRefMD(null);
  const [bad, setBad] = useStateMD(false);
  useEffectMD(() => { const el = ref.current; if (el) el.focus(); }, []);
  const send = () => {
    const v = (ref.current ? ref.current.value : '').trim();
    if (!v) { onCancel(); return; }
    const href = hrefMD(v);
    if (!href) { setBad(true); return; }
    onSubmit(v);
  };
  return ReactDOM.createPortal(
    <div style={{ ...mdPlace(anchor, 320, 96), ...mdPanel(light) }}>
      <input ref={ref} type="url" inputMode="url" spellCheck={false}
        aria-label="Address to embed"
        placeholder="Paste a link — video, picture or page"
        onChange={() => bad && setBad(false)}
        onKeyDown={(e) => {
          e.stopPropagation();                 // the editor's keys are not this input's
          if (e.key === 'Enter') { e.preventDefault(); send(); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
        }}
        onBlur={onCancel}
        style={{ width: '100%', boxSizing: 'border-box', padding: '9px 11px',
          borderRadius: 11, fontSize: 14, fontFamily: 'inherit', color: inkMD(0.92),
          background: inkMD(light ? 0.04 : 0.08), outline: 'none',
          border: `1px solid ${bad ? (light ? '#c0263f' : '#ff6b81') : mdAccentA(accent, 0.5)}` }} />
      <div style={{ padding: '6px 4px 2px', fontSize: 11.5, fontWeight: 500,
        color: bad ? (light ? '#c0263f' : '#ff6b81') : inkMD(0.42) }}>
        {bad ? 'That is not an address this can open' : 'Enter to place it · Esc to stop'}
      </div>
    </div>,
    document.body);
}

/* The "/database" answer: a new database, or a new view of one that already
   exists. Same surface grammar as the embed prompt — a field at the caret,
   Enter takes the highlighted row, Esc walks away, and picking an existing
   database embeds the SAME source, so the two views can never disagree. */
function MdDatabasePrompt({ anchor, light, accent, onPick, onCancel }) {
  const inputRef = useRefMD(null);
  const [query, setQuery] = useStateMD('');
  const [active, setActive] = useStateMD(0);
  useEffectMD(() => { const el = inputRef.current; if (el) el.focus(); }, []);
  const rows = [{ id: '', name: 'New database' }, ...(window.mdDatabasePickerRows ? window.mdDatabasePickerRows(query) : [])];
  const current = Math.min(active, rows.length - 1);
  const pick = (row) => onPick(row && row.id ? row.id : null);
  return ReactDOM.createPortal(
    <div role="listbox" aria-label="Choose a database" style={{ ...mdPlace(anchor, 272, 320), ...mdPanel(light) }}>
      <input ref={inputRef} type="text" spellCheck={false}
        aria-label="Search databases"
        placeholder="New database, or find one you have"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setActive(0); }}
        onKeyDown={(e) => {
          e.stopPropagation();                 // the editor's keys are not this input's
          if (e.key === 'Enter') { e.preventDefault(); pick(rows[current]); }
          if (e.key === 'Escape') { e.preventDefault(); onCancel(); }
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
            e.preventDefault();
            const step = e.key === 'ArrowDown' ? 1 : -1;
            setActive((current + step + rows.length) % rows.length);
          }
        }}
        onBlur={onCancel}
        style={{ width: '100%', boxSizing: 'border-box', padding: '9px 11px', marginBottom: 4,
          borderRadius: 11, fontSize: 14, fontFamily: 'inherit', color: inkMD(0.92),
          background: inkMD(light ? 0.04 : 0.08), outline: 'none',
          border: `1px solid ${mdAccentA(accent, 0.5)}` }} />
      {rows.map((row, i) => {
        const on = i === current;
        return (
          <div key={row.id || 'new'} role="option" aria-selected={on}
          onPointerDown={(e) => { e.preventDefault(); pick(row); }}
          onPointerEnter={() => setActive(i)}
          style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', height: 36,
            boxSizing: 'border-box', padding: '0 10px', borderRadius: 10, cursor: 'pointer',
            userSelect: 'none', WebkitUserSelect: 'none',
            background: on ? mdAccentA(accent, light ? 0.13 : 0.2) : 'transparent' }}>
            <span style={{ width: 24, height: 24, borderRadius: 7, flexShrink: 0, display: 'grid', placeItems: 'center',
              background: on ? mdAccentA(accent, light ? 0.18 : 0.26) : inkMD(light ? 0.06 : 0.1) }}>
              {row.id
                ? mdCmdGlyph('database', on ? mdAccentA(accent, 1) : inkMD(0.62))
                : <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M12 5v14M5 12h14" stroke={on ? mdAccentA(accent, 1) : inkMD(0.62)} strokeWidth="2.2" strokeLinecap="round"/></svg>}
            </span>
            <span dir="auto" style={{ fontSize: 13.5, fontWeight: row.id ? 500 : 650, color: inkMD(0.88),
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</span>
            {row.id ? null : <span style={{ marginInlineStart: 'auto', fontSize: 11, color: inkMD(0.4) }}>starts empty</span>}
          </div>
        );
      })}
    </div>,
    document.body);
}

/* ── undo / redo ─────────────────────────────────────────────────────────────
   The editor rewrites its own innerHTML on every keystroke, and that throws the
   browser's native undo stack away with it — measured 2026-07-30, before this
   existed: after typing, document.execCommand('undo') moved nothing at all, so
   ⌘Z and iOS shake-to-undo were both dead in compose. So we keep our own.

   Entries are whole source strings, not diffs. A record body is a few kilobytes at
   most and MD_HIST_MAX caps the ring, so the memory is a rounding error beside
   the full re-render we already pay per keystroke — and a whole-string snapshot
   cannot drift out of step with a document that is rebuilt wholesale anyway.

   Consecutive typing collapses into ONE entry, the way every editor a writer has
   ever used behaves: undo should take back a burst, not a letter. A burst ends
   after MD_HIST_GROUP ms of quiet, when the edit changes kind (typing → deleting),
   or when the edit is structural — a "/" command, a list continuation, a checkbox
   toggle, a paste, a Tab, an explicit line break. Those are single deliberate acts
   and each one earns its own step, which is why they pass kind === null.

   Each entry carries the caret AS IT WAS IN THAT TEXT, so undo returns the caret
   to where the writer was standing before the edit rather than dumping it at the
   end of the document. Only a collapsed caret is restored — a selection is not
   re-selected, which matches Notion and costs nothing we can see. */
const MD_HIST_MAX = 240;
const MD_HIST_GROUP = 500;

function mdHistory(src, links = []) {
  return { e: [{ src, links, caret: null }], i: 0, at: 0, kind: null };
}

/* Which edits are allowed to merge with the one before them. Anything not named
   here returns null and therefore stands alone: a paste, an autocorrect
   replacement, a drop, a formatting command. */
function mdEditKind(inputType) {
  if (!inputType) return 'type';
  if (inputType === 'insertText' || inputType === 'insertCompositionText') return 'type';
  if (inputType.indexOf('delete') === 0) return 'delete';
  return null;
}

/* Where the caret must have been standing for `prev` to have become `next`, read
   off the point the two texts part company. This is the fallback for when the
   pre-edit caret was never captured: beforeinput is the reliable source, but it
   does not fire for programmatic edits (document.execCommand raises `input` only,
   measured), and an editor that puts the caret in the wrong place after ⌘Z because
   one event went missing is worse than one that computes it. */
function mdDiffCaret(prev, next) {
  const n = Math.min(prev.length, next.length);
  let i = 0;
  while (i < n && prev.charCodeAt(i) === next.charCodeAt(i)) i++;
  // For an insertion the split point IS where the caret stood. For a deletion the
  // caret stood after the characters that went, so step back over them.
  return next.length >= prev.length ? i : i + (prev.length - next.length);
}

function mdHistPush(h, src, caret, preCaret, kind, links = []) {
  if (!h || src === h.e[h.i].src) return;   // a no-op edit never earns a step
  const now = Date.now();
  /* i > 0 because the bottom entry is the document as we found it: merging into it
     would leave the burst with nothing to undo back to. */
  const grouped = !!kind && kind === h.kind && now - h.at < MD_HIST_GROUP && h.i > 0;
  if (grouped) {
    // extend the burst in place — the entry BELOW already holds the caret from
    // where the burst began, which is where undo should land.
    h.e[h.i] = { src, links, caret };
  } else {
    if (preCaret != null) h.e[h.i].caret = preCaret;
    h.e.length = h.i + 1;                   // editing after an undo drops the redo tail
    h.e.push({ src, links, caret });
    h.i++;
    if (h.e.length > MD_HIST_MAX) { h.e.shift(); h.i--; }
  }
  h.at = now;
  h.kind = kind;
}

/* kind is cleared on both, so the next keystroke starts a fresh burst instead of
   merging into the state we just restored. */
function mdHistUndo(h) { if (!h || h.i <= 0) return null; h.kind = null; return h.e[--h.i]; }
function mdHistRedo(h) { if (!h || h.i >= h.e.length - 1) return null; h.kind = null; return h.e[++h.i]; }

/* `blockOf` / `renderBlock` / `blockFormats` / `formatBlock` are the four halves
   of one arrangement, and none of them knows what a video is: the host is asked
   what box to reserve, what to draw in it, what else that line could be drawn
   as, and how to write the line that says so. Same division md-list.ts and
   md-link.ts already have with this file — the grammar lives with the module
   that owns it, and this file lays it out. */
function LiveMarkdown({ value, onChange, placeholder, accent, autoFocus, fontSize = 15, minHeight = 0, fill = false, firstLineTitle = false, ariaLabel, style = {}, blockOf = null, renderBlock = null, blockFormats = null, formatBlock = null, describeUrl = null, wikiLinks = [], wikiRecords = [], onWikiLinksChange = null, onWikiOpen = null, onWikiCreate = null, tagOptions = [], tagApiRef = null, onCreateDatabase = null }) {
  const ref = useRefMD(null);
  const wrapRef = useRefMD(null);
  const last = useRefMD(value || '');
  const wikiRef = useRefMD(wikiBindingsMD ? wikiBindingsMD(wikiLinks, value || '') : []);
  /* Safari does not reveal which software keyboard is active. `beforeinput`
     does reveal its text before the browser inserts it, which is early enough
     to move an empty line's caret to the correct edge without a visible jump.
     Keep that latest strong direction so Enter can carry it to the next line. */
  const writingDirectionRef = useRefMD(
    strongDirectionMD ? (strongDirectionMD((value || '').split('\n').pop()) || 'ltr') : 'ltr');
  const lineDirectionsRef = useRefMD(
    lineDirectionsMD ? lineDirectionsMD(value || '', [], writingDirectionRef.current) : []);
  const renderedSourceRef = useRefMD(value || '');
  /* The one capital md-capitalize.ts last raised, and the one the writer took
     back. It is a ref and not state because it is read and rewritten inside a
     single input event, before paint, and nothing draws from it. */
  const capsRef = useRefMD(null);

  /* Where every reserved slot ended up, in the wrapper's own pixels, so the
     overlay can put a player exactly on top of one. Measured after each render
     rather than computed: the box is sized in CSS (a ratio against the measure,
     or the pixels somebody dragged to), and the only honest source for where a
     line landed is the line. */
  const [slots, setSlots] = useStateMD([]);
  /* The line the caret is standing on, for the block that wants to show it. It
     is state as well as a class because the overlay is React's and the class is
     not — see markActive, which owns the class and now also publishes this. */
  const [activeLine, setActiveLine] = useStateMD(-1);

  /* The undo ring, and the caret as it stood BEFORE the edit currently in flight.
     preRef is filled by the native beforeinput listener (which runs while the DOM
     is still untouched) for edits the browser performs, and by insertAtCaret for
     the ones we perform ourselves. It is cleared after every push so a stale
     position can never be attributed to the wrong edit. */
  const hist = useRefMD(null);
  if (hist.current === null) hist.current = mdHistory(value || '', wikiRef.current);
  const preRef = useRefMD(null);
  // The mount effect subscribes once and closes over the first render forever, so
  // what it calls has to be reachable through a ref that later renders refresh.
  const histFns = useRefMD(null);

  /* The "/" menu's state lives in BOTH a ref and React state, and it has to.
     The ref is the truth: the selectionchange listener is registered once with an
     empty dep array (it has to be — re-subscribing a document listener on every
     keystroke is how you get dropped events), so it closes over the first render
     forever and can only read a ref. The state exists to make React draw. */
  const slashRef = useRefMD(null);
  const [slash, setSlash] = useStateMD(null);
  const putSlash = (v) => {slashRef.current = v;setSlash(v);};
  const wikiMenuRef = useRefMD(null);
  const [wikiMenu, setWikiMenu] = useStateMD(null);
  const putWikiMenu = (v) => {wikiMenuRef.current = v;setWikiMenu(v);};
  const tagMenuRef = useRefMD(null);
  const [tagMenu, setTagMenu] = useStateMD(null);
  const putTagMenu = (v) => {tagMenuRef.current = v;setTagMenu(v);};

  /* The two surfaces that only exist for a moment: what to make of an address
     that was just pasted, and where to put one that "/embed" asked for. */
  const [pasteAs, setPasteAs] = useStateMD(null);
  const [prompt, setPrompt] = useStateMD(null);

  /* Mark the line the caret is on. Every OTHER line's syntax collapses to zero
     width (see the .md-on rules in the sheet), which buys the record ONE flush left
     edge instead of a stair-step — measured before this: the first word of a body
     line started at 30, a bullet at 45, an h1 at 51, an h3 at 68, all in the same
     document. The markers are not hidden, they are revealed where you are working:
     put the caret on a heading and its hashes come back, in place, editable.
     This is the live-preview bargain every serious markdown writer makes.
     It is a class toggle rather than a re-render on purpose — selectionchange fires
     constantly, and rebuilding innerHTML on caret movement would both cost more and
     risk disturbing the very caret that moved. */
  const markActive = () => {
    const el = ref.current; if (!el) return;
    let blk = null;
    const sel = window.getSelection();
    if (sel && sel.rangeCount && document.activeElement === el) {
      const n = sel.getRangeAt(0).startContainer;
      const e = n.nodeType === 1 ? n : n.parentElement;
      if (e && el.contains(e) && e.closest) blk = e.closest('.md-ln');
    }
    const prev = el.querySelector('.md-ln.md-on');
    if (prev === blk) return;
    if (prev) prev.classList.remove('md-on');
    if (blk) blk.classList.add('md-on');
    /* An embed cannot show the caret — it has no text to put one in — so it
       shows the same fact as a ring instead. Only the line NUMBER travels, and
       only when it changed, so caret movement inside prose never re-renders. */
    const at = blk && blk.hasAttribute('data-embed') ? +blk.getAttribute('data-embed') : -1;
    setActiveLine((v) => (v === at ? v : at));
  };

  /* Where the slots are now. Compared before it is stored, because this runs
     after every keystroke and a fresh array each time would re-render — and
     therefore reload — every player in the record. */
  const syncSlots = () => {
    const el = ref.current, wrap = wrapRef.current;
    if (!el || !wrap) return;
    const seen = Object.create(null);
    const next = [];
    el.querySelectorAll('.md-ln-embed').forEach((n) => {
      const key = n.getAttribute('data-key') || '';
      /* Two lines can carry the same address. The occurrence number is what
         keeps them two cards rather than one drawn twice — and it is stable as
         the writing around them moves, which is what stops a video reloading
         because a paragraph above it grew a word. */
      const nth = seen[key] = (seen[key] || 0) + 1;
      next.push({ id: `${key}#${nth}`, key, line: +n.getAttribute('data-embed'),
        left: n.offsetLeft, top: n.offsetTop, width: n.offsetWidth, height: n.offsetHeight });
    });
    setSlots((prev) => {
      if (prev.length === next.length && prev.every((p, i) => {
        const q = next[i];
        return p.id === q.id && p.line === q.line && p.left === q.left
          && p.top === q.top && p.width === q.width && p.height === q.height;
      })) return prev;
      return next;
    });
  };

  const render = (src, restore) => {
    const el = ref.current; if (!el) return;
    const off = restore != null ? restore : (document.activeElement === el ? mdCaretOffset(el) : null);
    const before = renderedSourceRef.current;
    if (before !== src && reconcileLineDirectionsMD) {
      const editAt = preRef.current != null ? preRef.current : mdDiffCaret(before, src);
      lineDirectionsRef.current = reconcileLineDirectionsMD(before, src,
        lineDirectionsRef.current, editAt, writingDirectionRef.current);
    } else if (lineDirectionsMD) {
      lineDirectionsRef.current = lineDirectionsMD(src, lineDirectionsRef.current,
        writingDirectionRef.current);
    }
    renderedSourceRef.current = src;
    el.innerHTML = mdSrcToHtml(src, accent, firstLineTitle, blockOf, wikiRef.current,
      lineDirectionsRef.current);
    if (document.activeElement === el && off != null) mdSetCaret(el, off);
    markActive();                        // innerHTML just dropped the previous mark
    syncSlots();                         // …and the boxes the overlay draws into
  };

  const publishWiki = (next, src = last.current) => {
    const clean = wikiBindingsMD ? wikiBindingsMD(next, src) : [];
    wikiRef.current = clean;
    if (onWikiLinksChange) onWikiLinksChange(clean);
    return clean;
  };
  const reconcileWiki = (before, after) => publishWiki(
    wikiReconcileMD ? wikiReconcileMD(before, after, wikiRef.current) : [], after);

  /* One line, replaced — the edit an embed's own controls make. Resizing writes
     a size into the line, removing writes an empty one, and both are one
     deliberate act and therefore one undo step. The block itself never touches
     the source: it is handed this and knows only what its own new line says. */
  const replaceLine = (idx, text) => {
    const el = ref.current; if (!el) return;
    const lines = last.current.split('\n');
    if (idx < 0 || idx >= lines.length || lines[idx] === text) return;
    const pre = document.activeElement === el ? mdCaretOffset(el) : null;
    let at = 0;
    for (let i = 0; i < idx; i++) at += lines[i].length + 1;
    lines[idx] = text;
    const src = lines.join('\n');
    reconcileWiki(last.current, src);
    last.current = src;
    render(src, at + text.length);
    mdHistPush(hist.current, src, at + text.length, pre, null, wikiRef.current);
    onChange && onChange(src);
  };

  /* ── "/" menu: open, track, close ────────────────────────────────────────
     Opened from the INPUT event rather than from keydown, because keydown does not
     see every "/" that reaches the document — an iOS keyboard, a dictation pass or
     an autocorrect substitution all insert text without a key event carrying it.
     What we can trust is the text itself, so the trigger is read from the diff:
     one character longer than before, and that character is a "/" sitting where a
     new word may begin (line start, or after a space). "and/or" and "24/7" have a
     letter or digit in front of the slash, so they never summon anything. */
  const slashAllowed = (src, off) => {
    if (off < 1 || src[off - 1] !== '/') return false;
    if (off > 1 && !/[\s]/.test(src[off - 2])) return false;
    // In title mode line 0 is the record's NAME, where block markers stay literal by
    // design (see mdRenderInner) — offering formats there would write syntax that
    // renders as syntax. The title is a title.
    if (firstLineTitle && src.lastIndexOf('\n', off - 2) < 0) return false;
    return true;
  };
  const closeSlash = () => {if (slashRef.current) putSlash(null);};

  /* Re-validate against the live source and caret. Called after every input and
     after every caret move, so the menu dies the moment its "/" is deleted, the
     caret leaves it, or the query grows a space — one exit condition per way the
     writer can change their mind, and none of them needs its own event. */
  const syncSlash = (src, prev) => {
    const el = ref.current;if (!el) return;
    const off = mdCaretOffset(el);
    const open = slashRef.current;
    if (off == null) return closeSlash();
    if (!open) {
      if (prev != null && src.length === prev.length + 1 && slashAllowed(src, off)) {
        const rect = mdCaretRect(el);
        if (rect) {putWikiMenu(null);putTagMenu(null);putSlash({ start: off - 1, query: '', idx: 0, left: rect.left, top: rect.top, bottom: rect.bottom });}
      }
      return;
    }
    const s = open.start;
    if (src[s] !== '/' || off < s + 1) return closeSlash();
    const query = src.slice(s + 1, off);
    if (/\s/.test(query) || query.length > 20) return closeSlash();
    if (query === open.query) return;
    /* The menu is anchored horizontally to the "/" itself — captured once, so it
       does not creep sideways while you filter — but vertically to the caret NOW,
       because the line can reflow or the record can scroll under it as the query
       grows and a menu pinned to a stale line is a menu in the wrong place. */
    const rect = mdCaretRect(el);
    putSlash({ ...open, query, idx: 0,
      top: rect ? rect.top : open.top, bottom: rect ? rect.bottom : open.bottom });
  };

  const wikiRows = (query) => {
    const q = (query || '').trim().toLocaleLowerCase();
    const scored = (Array.isArray(wikiRecords) ? wikiRecords : []).filter((row) => {
      return row && row.ref && typeof row.title === 'string'
        && (!q || row.title.toLocaleLowerCase().includes(q));
    }).map((row) => {
      const title = row.title.toLocaleLowerCase();
      const score = title === q ? 0 : title.startsWith(q) ? 1 : 2;
      return { ...row, key: `${row.ref.type}:${row.ref.id}`, score,
        glyph: row.ref.type === 'project' ? 'P' : row.ref.type === 'domain' ? 'D' : '•' };
    }).sort((a, b) => a.score - b.score || a.title.localeCompare(b.title)).slice(0, 18);
    if (q && !scored.some((row) => row.title.trim().toLocaleLowerCase() === q)) {
      scored.push({ key: `create:${q}`, create: true, title: query.trim(), subtitle: 'New 30 min record' });
    }
    return scored;
  };

  const closeWiki = () => {if (wikiMenuRef.current) putWikiMenu(null);};
  const syncWiki = (src) => {
    const el = ref.current;
    const off = el ? mdCaretOffset(el) : null;
    if (off == null) return closeWiki();
    const start = src.lastIndexOf('[[', off - 1);
    if (start < 0) return closeWiki();
    const query = src.slice(start + 2, off);
    if (query.length > 120 || /[\[\]\n]/.test(query)) return closeWiki();
    const rect = mdCaretRect(el);
    if (!rect) return closeWiki();
    const open = wikiMenuRef.current;
    putWikiMenu({ start, end: off, query, idx: open && open.start === start ? open.idx : 0,
      left: rect.left, top: rect.top, bottom: rect.bottom });
    if (slashRef.current) putSlash(null);
    if (tagMenuRef.current) putTagMenu(null);
  };

  const autoBindWiki = (src, links) => {
    if (!wikiLinksMD || !wikiBindMD) return links;
    let next = links;
    for (const token of wikiLinksMD(src, next)) {
      if (token.target) continue;
      const exact = (Array.isArray(wikiRecords) ? wikiRecords : []).filter((row) =>
        row && row.ref && typeof row.title === 'string'
        && row.title.trim().toLocaleLowerCase() === token.label.toLocaleLowerCase());
      if (exact.length === 1) next = wikiBindMD(src, next, token.start, exact[0].ref);
    }
    return next;
  };

  const pickWiki = (row) => {
    const open = wikiMenuRef.current;
    const el = ref.current;
    if (!open || !row || !el) return;
    const title = (row.title || '').trim().replace(/\[\[|\]\]/g, '').slice(0, 120);
    if (!title) return;
    let target = row.ref || null;
    if (row.create && onWikiCreate) target = onWikiCreate(title);
    if (!target) return;
    const before = last.current;
    const src = before.slice(0, open.start) + `[[${title}]]` + before.slice(open.end);
    let links = wikiReconcileMD ? wikiReconcileMD(before, src, wikiRef.current) : [];
    links = wikiBindMD ? wikiBindMD(src, links, open.start, target) : links;
    closeWiki();
    last.current = src;
    publishWiki(links, src);
    const caret = open.start + title.length + 4;
    render(src, caret);
    mdHistPush(hist.current, src, caret, open.end, null, wikiRef.current);
    onChange && onChange(src);
  };

  const tagRows = (query) => {
    const q = tagKeyMD ? tagKeyMD(query || '') : (query || '').toLocaleLowerCase();
    const rows = (Array.isArray(tagOptions) ? tagOptions : []).filter((row) => {
      if (!row || typeof row.name !== 'string') return false;
      const key = row.key || (tagKeyMD ? tagKeyMD(row.name) : row.name.toLocaleLowerCase());
      return !q || key.includes(q);
    }).map((row) => {
      const key = row.key || tagKeyMD(row.name);
      return { ...row, key, score: key === q ? 0 : key.startsWith(q) ? 1 : 2 };
    }).sort((a, b) => a.score - b.score || (b.count || 0) - (a.count || 0)
      || a.name.localeCompare(b.name)).slice(0, 18);
    const name = tagNameMD ? tagNameMD(query || '') : null;
    if (name && !rows.some((row) => row.key === tagKeyMD(name))) {
      rows.push({ key: `create:${tagKeyMD(name)}`, name, create: true, count: 0 });
    }
    return rows;
  };

  const closeTag = () => {if (tagMenuRef.current) putTagMenu(null);};
  const syncTag = (src, prev) => {
    const el = ref.current;
    const off = el ? mdCaretOffset(el) : null;
    const fragment = off != null && tagFragmentMD ? tagFragmentMD(src, off) : null;
    if (!fragment || (firstLineTitle && src.lastIndexOf('\n', fragment.start - 1) < 0)) return closeTag();
    const open = tagMenuRef.current;
    if (!open && (prev == null || src.length <= prev.length)) return;
    if (open && open.start !== fragment.start) return closeTag();
    const rect = mdCaretRect(el);
    if (!rect) return closeTag();
    putTagMenu({ ...fragment, idx: open ? open.idx : 0,
      left: rect.left, top: rect.top, bottom: rect.bottom });
    if (slashRef.current) putSlash(null);
    if (wikiMenuRef.current) putWikiMenu(null);
  };

  const pickTag = (row) => {
    const open = tagMenuRef.current, el = ref.current;
    if (!open || !row || !el || !tagCompleteMD) return;
    const before = last.current;
    const out = tagCompleteMD(before, open.start, open.end, row.name);
    if (!out) return;
    let src = out.text, caret = out.caret;
    if (caret === src.length || !/[\s.,;:!?)]/.test(src[caret] || '')) {
      src = src.slice(0, caret) + ' ' + src.slice(caret);
      caret++;
    }
    closeTag();
    reconcileWiki(before, src);
    last.current = src;
    render(src, caret);
    mdHistPush(hist.current, src, caret, open.end, null, wikiRef.current);
    onChange && onChange(src);
  };

  /* The inspector lives in compose's toolbar, outside this editor. It still
     asks the editor to perform its text edit so the operation enters the same
     undo ring as typing, link binding and formatting. */
  const editTag = (kind, name) => {
    const before = last.current;
    const out = kind === 'remove'
      ? (tagRemoveMD && tagRemoveMD(before, name))
      : (tagAddMD && tagAddMD(before, name));
    if (!out || out.text === before) return false;
    closeTag();
    reconcileWiki(before, out.text);
    last.current = out.text;
    render(out.text, out.caret);
    mdHistPush(hist.current, out.text, out.caret, null, null, wikiRef.current);
    onChange && onChange(out.text);
    return true;
  };
  if (tagApiRef) tagApiRef.current = {
    add: (name) => editTag('add', name),
    remove: (name) => editTag('remove', name),
  };
  useEffectMD(() => () => {
    if (tagApiRef) tagApiRef.current = null;
  }, [tagApiRef]);

  const runCmd = (cmd) => {
    const el = ref.current;const open = slashRef.current;
    if (!el || !open || !cmd) return;
    const src = last.current;
    const end = open.start + 1 + open.query.length;
    /* The database command asks WHICH database before it writes anything: one
       started empty, or a new view of one that already exists. Like the embed
       command it cannot finish on its own, so the query leaves the record
       first and its span is remembered — the picker's answer lands where
       "/database" stood. */
    if (cmd.database) {
      const cut = src.slice(0, open.start) + src.slice(end);
      putSlash(null);
      reconcileWiki(src, cut);
      last.current = cut;
      render(cut, open.start);
      onChange && onChange(cut);
      const rect = mdCaretRect(el);
      setPrompt({ kind: 'database', at: open.start, pre: end,
        left: rect ? rect.left : 0, top: rect ? rect.top : 0, bottom: rect ? rect.bottom : 0 });
      return;
    }
    /* The one command that cannot finish on its own: it needs an address. The
       query is taken away first, so the record does not sit with "/embed" in it
       while the prompt is open — and the span it occupied is remembered, because
       that is where the line will go. */
    if (cmd.embed) {
      const cut = src.slice(0, open.start) + src.slice(end);
      putSlash(null);
      reconcileWiki(src, cut);
      last.current = cut;
      render(cut, open.start);
      onChange && onChange(cut);
      const rect = mdCaretRect(el);
      setPrompt({ at: open.start, pre: end,
        left: rect ? rect.left : 0, top: rect ? rect.top : 0, bottom: rect ? rect.bottom : 0 });
      return;
    }
    const out = cmd.link ?
    mdApplyLink(src, open.start, end) :
    cmd.wrap ?
    mdApplyWrap(src, open.start, end, cmd.wrap) :
    mdApplyBlock(src, open.start, end, cmd.block);
    putSlash(null);
    if (!out) return;                     // md-link.ts declines rather than guess
    // A row is driven from pointerdown with the default prevented, so focus never
    // left — but a stray blur would strand the caret, and render() only restores a
    // caret into a focused element. Take it back before rewriting.
    if (document.activeElement !== el) {try {el.focus({ preventScroll: true });} catch (err) {el.focus();}}
    reconcileWiki(src, out.src);
    last.current = out.src;
    render(out.src, out.caret);
    // `end` is where the caret stood — at the tail of the "/" query it just consumed
    mdHistPush(hist.current, out.src, out.caret, end, null, wikiRef.current);
    onChange && onChange(out.src);
  };

  /* The address the prompt came back with, put on a line of its own. The focus
     goes back to the writing first: it left for the input, and a caret returned
     to a record it is no longer in is the one thing a prompt must not do. */
  const placeEmbed = (url) => {
    const open = prompt;
    setPrompt(null);
    const el = ref.current;
    if (!el || !open) return;
    try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
    const out = mdApplyEmbed(last.current, open.at, open.at, url.trim());
    reconcileWiki(last.current, out.src);
    last.current = out.src;
    render(out.src, out.caret);
    mdHistPush(hist.current, out.src, out.caret, open.at, null, wikiRef.current);
    onChange && onChange(out.src);
  };

  /* The database picker's answer. No id asks the host for a fresh database —
     the old behaviour, and the host may still say no — an id embeds a new view
     of a database that already exists. Both become the same quiet line, placed
     exactly like an embed that came back with its address. */
  const placeDatabase = (databaseId) => {
    const open = prompt;
    setPrompt(null);
    const el = ref.current;
    if (!el || !open) return;
    const line = databaseId
      ? (window.mdDatabaseLine ? window.mdDatabaseLine(databaseId) : '')
      : (onCreateDatabase && onCreateDatabase());
    try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
    if (!line) return;
    const out = mdApplyEmbed(last.current, open.at, open.at, line);
    reconcileWiki(last.current, out.src);
    last.current = out.src;
    render(out.src, out.caret);
    mdHistPush(hist.current, out.src, out.caret, open.at, null, wikiRef.current);
    onChange && onChange(out.src);
  };

  /* A row of the paste menu, taken. The row that is already on is a no-op rather
     than a rewrite — picking what you already have should not put a step in the
     record's history to undo. Everything else is one line replaced, which is
     what makes ⌘Z after it give the writer their paste back exactly. */
  const pickFormat = (row) => {
    const open = pasteAs;
    setPasteAs(null);
    if (!open || !row || row.on || !formatBlock) return;
    const line = last.current.split('\n')[open.line];
    if (line == null) return;
    replaceLine(open.line, formatBlock(line, row.id));
  };

  /* Restoring an entry is the one write that does NOT push — it is a move along
     the ring, not an edit to it. */
  const applyHist = (entry) => {
    if (!entry) return;
    const el = ref.current;
    if (el && document.activeElement !== el) {
      try { el.focus({ preventScroll: true }); } catch (err) { el.focus(); }
    }
    closeSlash();
    putWikiMenu(null);
    putTagMenu(null);
    preRef.current = null;
    last.current = entry.src;
    publishWiki(entry.links || [], entry.src);
    render(entry.src, entry.caret != null ? Math.min(entry.caret, entry.src.length) : entry.src.length);
    onChange && onChange(entry.src);
  };
  const doUndo = () => applyHist(mdHistUndo(hist.current));
  const doRedo = () => applyHist(mdHistRedo(hist.current));
  /* The mount effect's listener closes over the first render forever, so
     everything it calls is reached through this ref, which every render
     refreshes. */
  histFns.current = { undo: doUndo, redo: doRedo,
    embedAt: (off) => embedLineAt(off),
    typeAfter: (idx, text) => {
      const el = ref.current; if (!el) return;
      mdSetCaret(el, lineSpan(idx).end);
      insertAtCaret('\n' + text, null);
    } };

  /* ⌘K, from wherever the writer is standing. With the "/" menu open it means
     "the link command" and routes through runCmd, which is the only thing that
     knows how to consume the query — one applier, two ways in. */
  const doLink = () => {
    const el = ref.current;if (!el) return;
    if (slashRef.current) {runCmd(MD_LINK_CMD);return;}
    const range = mdSelRange(el);
    if (!range) return;
    const out = insertLinkMD(last.current, range.start, range.end);
    if (!out) return;                   // a selection across lines is not a label
    reconcileWiki(last.current, out.src);
    last.current = out.src;
    render(out.src, out.caret);
    // range.end is where the caret stood; a link is one deliberate act, one step
    mdHistPush(hist.current, out.src, out.caret, range.end, null, wikiRef.current);
    onChange && onChange(out.src);
  };

  /* The link the caret is standing IN, read back off the rendered spans rather
     than re-parsed from the source. The render has already decided what is a
     link and what is safe to open, and two answers to that question is one too
     many. Note this is the label of a `[…](…)`, or anywhere inside a bare URL —
     with the caret in the target slot you are editing the address, not reading
     the link, so nothing opens. */
  const linkAtCaret = () => {
    const el = ref.current;if (!el) return null;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return null;
    const n = sel.getRangeAt(0).startContainer;
    const e = n.nodeType === 1 ? n : n.parentElement;
    if (!e || !el.contains(e) || !e.closest) return null;
    return e.closest('.md-link');
  };

  // external value change (draft restore / switching record) — don't clobber while typing
  useLayoutEffectMD(() => {
    const incoming = wikiBindingsMD ? wikiBindingsMD(wikiLinks, value || '') : [];
    if ((value || '') !== last.current) {
      last.current = value || '';
      wikiRef.current = incoming;
      writingDirectionRef.current = strongDirectionMD
        ? (strongDirectionMD((value || '').split('\n').pop()) || 'ltr') : 'ltr';
      lineDirectionsRef.current = lineDirectionsMD
        ? lineDirectionsMD(value || '', [], writingDirectionRef.current) : [];
      renderedSourceRef.current = value || '';
      // A different document gets a different history. Undoing across a record switch
      // would rewrite a record the writer is no longer looking at.
      hist.current = mdHistory(value || '', incoming);
      preRef.current = null;
      closeSlash();
      putWikiMenu(null);
      putTagMenu(null);
      render(value || '', null);
    } else {
      wikiRef.current = incoming;
      render(last.current, null);
    }
  }, [value, wikiLinks]);

  /* The caret panels are ONE layer on the dismiss arbiter, standing only while
     one of them is open — a closed menu leaves Escape to compose (where it
     dismisses the record). Innermost first: the prompt is the only one holding
     the focus. The layer owns every field: the caret sits in the editor (or in
     a prompt's own input), and one press must close the panel, not blur the
     writing out from under it. */
  window.useMdDismissLayer(() => {
    if (prompt) { setPrompt(null); const el = ref.current; if (el) el.focus(); return; }
    if (pasteAs) { setPasteAs(null); return; }
    if (tagMenu) { putTagMenu(null); return; }
    if (wikiMenu) { putWikiMenu(null); return; }
    putSlash(null);
  }, !!(slash || wikiMenu || tagMenu || pasteAs || prompt), () => true, 'menu');

  /* The paste menu is an offer, not a question, so any sign that the writer has
     moved on takes it away: the next keystroke, or a click anywhere. It never
     waits to be dismissed.
     Deliberately NOT selectionchange: the render that placed the pasted text
     also put the caret back, and that fires one — the menu would close in the
     same tick it opened. A caret cannot move without a key or a pointer anyway. */
  useEffectMD(() => {
    if (!pasteAs) return undefined;
    const el = ref.current;
    const go = () => setPasteAs(null);
    const onKey = (e) => { if (e.key !== 'Escape') go(); };
    if (el) el.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', go);
    return () => {
      if (el) el.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', go);
    };
  }, [pasteAs]);

  useEffectMD(() => {
    render(last.current);
    // Focus SYNCHRONOUSLY in the mount effect, not in a rAF: on iOS the tap
    // that opened compose primed the keyboard against a hidden proxy input, and
    // every frame we wait before taking the focus over is a frame of the wrong
    // (light, proxy-owned) keyboard. rAF can be throttled for seconds while the
    // keyboard presentation animates; a plain effect runs now.
    if (autoFocus) { const el = ref.current; if (el) { try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); } mdSetCaret(el, last.current.length); } }
    // selectionchange is the only event that fires for a caret moved by an arrow
    // key, a click or a drag — none of which produce input, so none of which reach
    // render(). It lives on document, not the element; there is no element-scoped
    // version of it.
    // Also the only signal that the caret has WALKED OUT of an open "/" query —
    // an arrow key or a click moves it without producing input.
    const onSel = () => {
      markActive();
      if (slashRef.current) syncSlash(last.current, null);
      if (wikiMenuRef.current) syncWiki(last.current);
      if (tagMenuRef.current) syncTag(last.current, null);
    };
    document.addEventListener('selectionchange', onSel);

    /* beforeinput, natively, for two things keydown cannot give us.
       One: it runs while the DOM is still as it was, so it is the only moment the
       caret can be read from BEFORE the edit — which is the position undo has to
       return to.
       Two: it is how undo reaches us when no key was pressed. iOS shake-to-undo,
       the iPad keyboard's undo key and the Edit menu all arrive as an inputType of
       historyUndo / historyRedo and nothing else. React's onBeforeInput is a
       synthetic shim that does not carry inputType, so this has to be the real one. */
    const el2 = ref.current;
    const onBeforeInput = (ev) => {
      const fns = histFns.current;
      if (ev.inputType === 'historyUndo' || ev.inputType === 'historyRedo') {
        ev.preventDefault();                 // the browser's own stack is empty anyway
        if (fns) (ev.inputType === 'historyUndo' ? fns.undo : fns.redo)();
        return;
      }
      /* A software keyboard's locale is private browser state. Its first strong
         character is not: beforeinput gives it to us before paint. On a neutral
         line, stamp that direction immediately so the character and caret arrive
         at the correct edge in the same frame. In a mixed line only remember the
         keyboard direction for the next Enter; the first strong character keeps
         ownership of the current line, matching Notes and Unicode bidi. */
      if (typeof ev.data === 'string' && ev.data && strongDirectionMD) {
        const direction = strongDirectionMD(ev.data);
        if (direction) {
          writingDirectionRef.current = direction;
          const offset = el2 ? mdCaretOffset(el2) : null;
          const line = directionLineAtMD && offset != null
            ? directionLineAtMD(last.current, offset) : -1;
          const text = line >= 0 ? last.current.split('\n')[line] : null;
          if (line >= 0 && !strongDirectionMD(text)) {
            lineDirectionsRef.current[line] = direction;
            const block = el2 && el2.querySelectorAll('.md-ln')[line];
            if (block) block.setAttribute('dir', direction);
          }
        }
      }
      /* TYPING NEXT TO A CARD STARTS A LINE UNDER IT. A click anywhere in a
         card's slot — including the strip of padding below the last one in the
         record — puts the caret on that card's line, because that is the line
         the click is nearest to. Left alone, the first letter typed would be
         appended to the address, the line would stop being a link on its own,
         and the card would vanish mid-keystroke leaving raw markdown behind.
         So the letter goes where the writer plainly meant it: a new line, after
         the card. This is also what makes a record that ENDS in an embed
         writable at all. */
      if (ev.inputType === 'insertText' && fns && typeof ev.data === 'string' && ev.data) {
        const sel = window.getSelection();
        if (sel && sel.rangeCount && sel.isCollapsed) {
          const at = fns.embedAt(mdCaretOffset(el2));
          if (at >= 0) {
            ev.preventDefault();
            fns.typeAfter(at, ev.data);
            return;
          }
        }
      }
      preRef.current = el2 ? mdCaretOffset(el2) : null;
    };
    if (el2) el2.addEventListener('beforeinput', onBeforeInput);

    /* A slot's box is CSS — a ratio against the writing's own measure — so it
       moves whenever the column does: a window resize, a sidebar opening, the
       panel becoming the full sheet. Nothing about that produces input, so the
       overlay would otherwise sit where the writing used to be. */
    let ro = null;
    if (typeof ResizeObserver === 'function' && wrapRef.current) {
      ro = new ResizeObserver(() => syncSlots());
      ro.observe(wrapRef.current);
    }

    return () => {
      document.removeEventListener('selectionchange', onSel);
      if (el2) el2.removeEventListener('beforeinput', onBeforeInput);
      if (ro) ro.disconnect();
    };
    // eslint-disable-next-line
  }, []);

  /* Which embed line the caret is standing on, or -1. Read from the rendered
     block rather than re-parsed, so the keys below and the ring the reader sees
     can never disagree about what the caret is on. */
  const embedLineAt = (offset) => {
    const el = ref.current;
    if (!el || offset == null) return -1;
    const src = last.current;
    let at = 0;
    const lines = src.split('\n');
    for (let i = 0; i < lines.length; i++) {
      const end = at + lines[i].length;
      if (offset >= at && offset <= end) {
        const blk = el.querySelector(`.md-ln-embed[data-embed="${i}"]`);
        return blk ? i : -1;
      }
      at = end + 1;
    }
    return -1;
  };

  /* Which line an offset is on. */
  const lineIndexAt = (offset) => {
    const lines = last.current.split('\n');
    let at = 0;
    for (let i = 0; i < lines.length; i++) {
      const end = at + lines[i].length;
      if (offset <= end) return i;
      at = end + 1;
    }
    return lines.length - 1;
  };

  /* The [start, end) of a line, as source offsets. */
  const lineSpan = (idx) => {
    const lines = last.current.split('\n');
    let at = 0;
    for (let i = 0; i < idx; i++) at += lines[i].length + 1;
    return { start: at, end: at + (lines[idx] == null ? 0 : lines[idx].length) };
  };

  const emit = (kind) => {
    const el = ref.current; if (!el) return;
    let src = el.textContent;          // verbatim — equals the source string
    let caret = mdCaretOffset(el);
    /* `[]` is the compact writing gesture; canonical Markdown remains the
       storage format. Convert before rendering so the new task gets its stable
       visible caret in the same input event that created it. */
    const shortcut = todoShortcutMD && todoShortcutMD(src, caret, firstLineTitle);
    if (shortcut) { src = shortcut.src; caret = shortcut.caret; }
    const prev = last.current;
    /* A line begins with a capital, which every other surface the writer types
       on has done for them for years. Here rather than on the element, because
       `autocapitalize` is a hint to a software keyboard and says nothing at all
       on a laptop. md-capitalize.ts decides; this only carries the memo that
       lets one Backspace overrule it. */
    if (autoCapitalizeMD) {
      const caps = autoCapitalizeMD(prev, src, caret, capsRef.current, firstLineTitle);
      capsRef.current = caps.memo;
      src = caps.src; caret = caps.caret;
    }
    /* Some software keyboards, dictation engines and browser automation paths
       omit beforeinput.data. Recover the actual inserted run from the source
       diff before rendering, so their latest script still follows Enter. */
    const insertedDirection = strongDirectionMD && insertedTextMD
      ? strongDirectionMD(insertedTextMD(prev, src)) : null;
    if (insertedDirection) writingDirectionRef.current = insertedDirection;
    let links = wikiReconcileMD ? wikiReconcileMD(prev, src, wikiRef.current) : [];
    links = autoBindWiki(src, links);
    last.current = src;
    publishWiki(links, src);
    render(src, caret);                 // restyle in place (caret preserved)
    // after render, so the caret being recorded is the settled one
    const pre = preRef.current != null ? preRef.current : mdDiffCaret(prev, src);
    mdHistPush(hist.current, src, mdCaretOffset(el), pre, shortcut ? null : kind, wikiRef.current);
    preRef.current = null;
    onChange && onChange(src);
    syncSlash(src, prev);               // after render — the caret is back in place
    syncWiki(src);
    syncTag(src, prev);
  };

  /* Deterministic caret insert (execCommand('insertText','\n') is unreliable for
     newlines). Nothing here goes through beforeinput — we prevented the default
     that would have raised it — so the pre-edit caret is taken here instead. */
  const insertAtCaret = (text, kind) => {
    const el = ref.current; if (!el) return;
    preRef.current = mdCaretOffset(el);
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) { el.textContent += text; emit(kind); return; }
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node); range.collapse(true);
    sel.removeAllRanges(); sel.addRange(range);
    emit(kind);
  };

  /* Apply a source rewrite md-list.ts worked out: a list continuation, an indent,
     an outdent. Each is one deliberate act (kind null), so each earns its own undo
     step rather than merging into the burst of typing around it — ⌘Z after Tab
     should take back the Tab, not the sentence. `pre` is where the caret stood
     before, which is where undo puts it back. */
  const applyEdit = (step, pre) => {
    reconcileWiki(last.current, step.src);
    last.current = step.src;
    render(step.src, step.caret);
    mdHistPush(hist.current, step.src, step.caret, pre, null, wikiRef.current);
    onChange && onChange(step.src);
  };

  const onKeyDown = (e) => {
    /* Undo first, ahead of everything — including the open menu, which applyHist
       closes on the way past. preventDefault matters twice over: it stops the
       browser running its own (empty) undo, and it stops the beforeinput that
       would otherwise arrive as historyUndo and undo a second time. */
    if ((e.metaKey || e.ctrlKey) && !e.altKey) {
      const k = e.key.toLowerCase();
      if (k === 'z' || k === 'y') {
        e.preventDefault();
        // ⌘⇧Z is redo everywhere; Ctrl+Y is the Windows spelling of the same thing.
        if (k === 'y' || e.shiftKey) doRedo(); else doUndo();
        return;
      }
      /* ⌘K is the link key in every editor a writer has already met — Notion,
         Slack, Docs, GitHub — and it has been on the missing list since the
         "/" menu was built. */
      if (k === 'k') {e.preventDefault();doLink();return;}
      /* Following a link needs a key, or it is a pointer-only control inside the
         one surface a keyboard user cannot tab through: a contentEditable has
         exactly one cursor and it is the caret. So ⌘↵ opens what the caret is
         standing in, and falls through to an ordinary line break when that is
         nothing — which is what ⌘↵ did here before. */
      if (k === 'enter') {
        const node = linkAtCaret();
        if (node && mdOpenLink(node)) {e.preventDefault();return;}
      }
    }
    const tagOpen = tagMenuRef.current;
    if (tagOpen) {
      const rows = tagRows(tagOpen.query);
      const idx = Math.min(tagOpen.idx, Math.max(0, rows.length - 1));
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!rows.length) return;
        const step = e.key === 'ArrowDown' ? 1 : -1;
        putTagMenu({ ...tagOpen, idx: (idx + step + rows.length) % rows.length });
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && rows.length) {
        e.preventDefault();
        pickTag(rows[idx]);
        return;
      }
    }
    const wikiOpen = wikiMenuRef.current;
    if (wikiOpen) {
      const rows = wikiRows(wikiOpen.query);
      const idx = Math.min(wikiOpen.idx, Math.max(0, rows.length - 1));
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!rows.length) return;
        const step = e.key === 'ArrowDown' ? 1 : -1;
        putWikiMenu({ ...wikiOpen, idx: (idx + step + rows.length) % rows.length });
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && rows.length) {
        e.preventDefault();
        pickWiki(rows[idx]);
        return;
      }
    }
    /* The open menu takes the navigation keys first — while it is up, Enter means
       "use this format", not "new line". Escape is not handled here at all: the
       open panel holds the top layer of the dismiss arbiter, which acts on
       window capture before anything bound to this element could. */
    const open = slashRef.current;
    if (open) {
      const items = mdCmdMatch(open.query).filter((cmd) => !cmd.database || onCreateDatabase);
      const idx = Math.min(open.idx, Math.max(0, items.length - 1));
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        if (!items.length) return;
        const step = e.key === 'ArrowDown' ? 1 : -1;
        putSlash({ ...open, idx: (idx + step + items.length) % items.length });
        return;
      }
      if ((e.key === 'Enter' || e.key === 'Tab') && items.length) {
        e.preventDefault();
        runCmd(items[idx]);
        return;
      }
    }
    /* ── standing on an embed ────────────────────────────────────────────────
       Its line is a URL the reader never sees, sitting at zero width, and to
       the browser that is still forty characters to walk through one press at a
       time. So the four keys that would walk them are given the meaning the
       card has: it is ONE thing.
         ← →      step off it, to the line on that side
         ⌫        take the card out, leaving the empty line it stood on
         ⏎        a new line under it, wherever in the line the caret happens
                  to be — you cannot see where that is, so it must not matter
       Everything else falls through untouched: typing beside it is still
       typing, and what you type stops being an address and becomes prose,
       which is exactly what the writer just said they wanted. */
    const el0 = ref.current;
    const sel0 = el0 && window.getSelection();
    if (el0 && sel0 && sel0.rangeCount && sel0.isCollapsed && !slashRef.current
      && (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'Enter'
        || e.key === 'Backspace')) {
      const off0 = mdCaretOffset(el0);
      const at = embedLineAt(off0);
      if (at >= 0) {
        const span = lineSpan(at);
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
          e.preventDefault();
          const to = e.key === 'ArrowLeft'
            ? Math.max(0, span.start - 1)
            : Math.min(last.current.length, span.end + 1);
          mdSetCaret(el0, to);
          markActive();
          return;
        }
        if (e.key === 'Backspace') {
          e.preventDefault();
          replaceLine(at, '');
          return;
        }
        // Enter: always after the card, never inside its address
        e.preventDefault();
        mdSetCaret(el0, span.end);
        insertAtCaret('\n', null);
        return;
      }
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      /* Only a collapsed caret gets the smart path. With a range selected, Enter
         means "replace this with a line break" — splicing a marker in as well would
         be guessing at two intentions from one keystroke. */
      const el = ref.current;
      const sel = window.getSelection();
      if (el && sel && sel.rangeCount && sel.isCollapsed) {
        const off = mdCaretOffset(el);
        const step = off == null ? null : listContinueMD(last.current, off, firstLineTitle);
        if (step) { applyEdit(step, off); return; }
      }
      insertAtCaret('\n', null);        // a line break ends the typing burst
    }
    /* Tab is the indent key inside a list and nothing else. Outside one it is
       still two spaces, which is what it always was here — the writer asked for
       whitespace and there is no list for them to have meant instead.
       A REFUSED indent (the first item of a list, the depth cap) returns the
       source unchanged, and that is deliberately not the same as "not a list":
       the key had a meaning here, it simply cannot be honoured, so it does
       nothing rather than quietly inserting spaces into a bullet. */
    else if (e.key === 'Tab') {
      e.preventDefault();
      const el = ref.current;
      const range = el ? mdSelRange(el) : null;
      const step = range ?
        (e.shiftKey ? listOutdentMD(last.current, range.start, range.end) :
          listIndentMD(last.current, range.start, range.end)) : null;
      if (step) { if (step.src !== last.current) applyEdit(step, range.end); return; }
      if (!e.shiftKey) insertAtCaret('  ', null);
    }
    else if (e.key === 'Backspace' || e.key === 'Delete') {
      /* Joining two lines is the one deletion we cannot leave to the browser. The
         newline being deleted is not a character sitting beside the caret any more —
         it is inside a hidden span belonging to the PREVIOUS block, and what an
         engine does when asked to delete backwards across a block boundary into
         display-collapsed content is its own business and differs between them.
         So: if the character about to go is a newline, splice the source ourselves
         and re-render. Every other keystroke is left alone — ordinary text lives in
         an ordinary text node, native editing handles it, and the re-render on input
         restyles it. Selections are left alone too; only a collapsed caret sitting
         exactly on a line seam needs this. */
      const el = ref.current; if (!el) return;
      const sel = window.getSelection();
      if (!sel || !sel.rangeCount || !sel.isCollapsed) return;
      const off = mdCaretOffset(el);
      if (off == null) return;
      const src = last.current;
      /* Un-format before delete. The writer picked "To-do" from the menu and
         never typed "- [ ] "; making them un-type it, one character at a time,
         with the raw syntax on screen for five of those presses, is the shortcut
         charging rent. mdUnformat answers null for every ordinary keystroke, so
         this costs one string scan on the line the caret is in and nothing else.
         It goes in as its OWN history step (applyEdit pushes with no merge kind)
         — ⌘Z after this puts the to-do back, rather than rewinding whatever
         typing burst happened to precede it. */
      if (e.key === 'Backspace') {
        const step = unformatMD(src, off, firstLineTitle);
        if (step) { e.preventDefault(); applyEdit(step, off); return; }
      }
      const cut = e.key === 'Backspace' ? off - 1 : off;
      if (cut < 0 || cut >= src.length || src[cut] !== '\n') return;
      e.preventDefault();
      const next = src.slice(0, cut) + src.slice(cut + 1);
      reconcileWiki(src, next);
      last.current = next;
      render(next, cut);                 // caret lands on the seam it just closed
      // 'delete' so a run of backspaces stays ONE step even as it crosses seams
      mdHistPush(hist.current, next, cut, off, 'delete', wikiRef.current);
      onChange && onChange(next);
    }
  };
  const onPaste = (e) => {
    e.preventDefault();
    const t = (e.clipboardData || window.clipboardData).getData('text/plain');
    const pastedDirection = strongDirectionMD && strongDirectionMD(t);
    if (pastedDirection) writingDirectionRef.current = pastedDirection;
    /* Same rule as typing: what lands beside a card lands UNDER it, never
       inside its address. See the beforeinput handler. */
    const el0 = ref.current;
    const at0 = el0 ? embedLineAt(mdCaretOffset(el0)) : -1;
    if (at0 >= 0) {
      mdSetCaret(el0, lineSpan(at0).end);
      insertAtCaret('\n' + t, null);
    } else insertAtCaret(t, null);       // a paste is one deliberate act, one step
    /* An address, alone on its line, is a card by the time this runs — the rule
       ran during the render insertAtCaret just did. So the only question left is
       whether that is what was wanted, and MdPasteAs is where it is asked. */
    const el = ref.current;
    const url = (t || '').trim();
    if (!el || !blockOf || !url || /\s/.test(url)) return;
    const off = mdCaretOffset(el);
    if (off == null) return;
    const idx = lineIndexAt(off);
    const line = last.current.split('\n')[idx];
    if (line == null || line.trim() !== url) return;   // pasted into a sentence
    const box = blockOf(line, idx);
    if (!box) return;
    /* Nothing to ask when there is nothing on offer — a document dropped into
       the writing is a document, and a menu with one row in it is a menu that
       wastes the one moment the writer was paying attention. */
    const rows = blockFormats ? blockFormats(line) : [];
    if (rows.length < 2) return;
    const rect = mdCaretRect(el);
    if (rect) setPasteAs({ line: idx, url, rows,
      left: rect.left, top: rect.top, bottom: rect.bottom });
  };
  const onClick = (e) => {
    const box = e.target.closest && e.target.closest('[data-todo]');
    if (box) {
      e.preventDefault();
      const idx = +box.getAttribute('data-line');
      const lines = last.current.split('\n');
      if (lines[idx] != null) {
        const el = ref.current;
        const pre = el ? mdCaretOffset(el) : null;
        // the pad is part of the line's identity now — a nested to-do that is
        // ticked must stay nested, so the capture puts it back
        lines[idx] = lines[idx].replace(/^([ \t]*)- \[( |x|X)\] /,
          (mm, p, c) => p + (c.toLowerCase() === 'x' ? '- [ ] ' : '- [x] '));
        const src = lines.join('\n');
        reconcileWiki(last.current, src);
        last.current = src;
        render(src, null);
        mdHistPush(hist.current, src, pre, pre, null, wikiRef.current);
        onChange && onChange(src);
      }
      return;
    }
    /* A plain click follows a link. In a contentEditable a click normally only
       places the caret, so this is a deliberate choice and it has a cost worth
       stating: you cannot TAP into a bare URL to fix a typo in it, because the
       tap opens it instead. Two escape hatches, neither of them a click — drag
       across it (the selection test below), and on iOS the long-press
       magnifier, which places a caret without ever producing a click.
       A `[label](url)` has a third: its target slot is dimmed syntax, not part
       of the link span, so clicking there edits the address as normal. */
    const wiki = e.target.closest && e.target.closest('.md-wiki');
    if (wiki) {
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) return;
      const start = +wiki.getAttribute('data-wiki-start');
      const token = wikiLinksMD && wikiLinksMD(last.current, wikiRef.current)
        .find((row) => row.start === start);
      if (!token) return;
      e.preventDefault();
      if (token.target && onWikiOpen) {onWikiOpen(token.target);return;}
      const el = ref.current;
      if (!el) return;
      try {el.focus({ preventScroll: true });} catch (err) {el.focus();}
      mdSetCaret(el, token.innerEnd);
      syncWiki(last.current);
      return;
    }
    const a = e.target.closest && e.target.closest('.md-link');
    if (!a) return;
    // a drag that happened to end on a link is a selection, not a click on it
    const sel = window.getSelection();
    if (sel && !sel.isCollapsed) return;
    if (mdOpenLink(a)) e.preventDefault();
  };

  const cmdItems = slash ? mdCmdMatch(slash.query).filter((cmd) => !cmd.database || onCreateDatabase) : [];
  const cmdActive = Math.min(slash ? slash.idx : 0, Math.max(0, cmdItems.length - 1));
  const wikiItems = wikiMenu ? wikiRows(wikiMenu.query) : [];
  const wikiActive = Math.min(wikiMenu ? wikiMenu.idx : 0, Math.max(0, wikiItems.length - 1));
  const tagItems = tagMenu ? tagRows(tagMenu.query) : [];
  const tagActive = Math.min(tagMenu ? tagMenu.idx : 0, Math.max(0, tagItems.length - 1));

  /* ── the writing takes the whole surface ──────────────────────────────────
     `fill` is for a host that has given this editor a fixed height to live in —
     the record sheet — and it buys two things a content-sized box cannot:

       • THE PAGE IS THE TARGET. A contentEditable is only as tall as its text,
         so clicking the empty space beneath a two-line record used to hit the
         scroller and do nothing. Filled, that space is the editor, and the
         browser's own hit-testing puts the caret at the end — which is what
         everybody means by clicking under the last line of a page.
       • ROOM UNDER THE LAST LINE. `min-height` counts padding (box-sizing is
         border-box, set once in the sheet), so the runway is INSIDE the fill
         while the record is short — nothing to scroll, no empty space to fall
         into — and beyond it the moment the writing is longer than the fold.
         That is the whole of it: one declaration, no measuring, and the writer
         stops typing blank lines to get their last paragraph off the floor. */
  const fillStyle = fill
    ? { flex: 1, minHeight: '100%', paddingBottom: 'var(--md-runway, 34vh)' }
    : { minHeight };

  return (
    <div ref={wrapRef}
      style={fill ? { position: 'relative', flex: 1, display: 'flex', flexDirection: 'column' }
        : { position: 'relative' }}>
    <div
      ref={ref}
      className="md-live"
      contentEditable
      suppressContentEditableWarning
      role="textbox"
      aria-multiline="true"
      // the editor stays the focused element while the menu is open, so it is the
      // editor that has to announce the list and which row is current
      aria-expanded={slash || wikiMenu || tagMenu ? true : undefined}
      aria-controls={slash ? 'md-cmd-menu' : wikiMenu ? 'md-wiki-menu' : tagMenu ? 'md-tag-menu' : undefined}
      aria-activedescendant={slash && cmdItems.length ? 'md-cmd-' + cmdItems[cmdActive].id
        : wikiMenu && wikiItems.length ? `md-wiki-${wikiActive}`
        : tagMenu && tagItems.length ? `md-tag-${tagActive}` : undefined}
      aria-label={ariaLabel || placeholder || 'Text'}
      data-placeholder={placeholder || ''}
      onInput={(e) => emit(mdEditKind(e.nativeEvent && e.nativeEvent.inputType))}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      onClick={onClick}
      // blur does not move the selection, so selectionchange never fires for it —
      // without this the line you left keeps its syntax showing on a record nobody
      // is editing any more.
      onBlur={markActive}
      onFocus={markActive}
      spellCheck={true}
      style={{
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', outline: 'none',
        // 1.6 is the body measure for 15–17px prose — the leading the eye wants for
        // continuous reading, where 1.5 is a UI-label number. Block classes in the
        // sheet tighten it back up for headings, which want 1.15–1.25, not 1.6.
        fontSize, lineHeight: 1.6, color: inkMD(0.9), ...fillStyle,
        /* The caret is the text, not the app: ink — white on the dark theme, black
           on the light one — taking the colour of the block it stands in, which is
           how the title line's own ink reaches it. It was the accent purple, which
           read as a UI element visiting the record rather than as the writing's own
           cursor.
           `currentColor` and not `auto`, which is NOT a synonym for it. The spec
           leaves `auto` to the UA, and WebKit spends that latitude on the system
           tint: on iOS and iPadOS the caret came out the platform blue-purple no
           matter what the record said, which is exactly the borrowed-UI look this was
           meant to end. Blink does resolve `auto` to currentColor, so the bug was
           invisible in the desktop browser we build in. Naming the colour outright
           leaves the UA nothing to decide.
           Its HEIGHT follows the font at the insertion point for free, and that is
           the other half of the same idea: the caret is as tall as an h1 in an h1
           and as tall as body text in a paragraph, because the block carries the
           size (see .md-ln-h1 and friends in the sheet). Nothing to set. */
        caretColor: 'currentColor', ...style,
      }}
    />
    {/* ── the players ────────────────────────────────────────────────────────
        Laid over the boxes the editor reserved, in a layer of its own. It is
        NOT inside the contentEditable, so an iframe here survives every
        keystroke; it is not a sibling below the writing either, so what you see
        is still one document with pictures in it.

        pointerEvents is none on the layer and auto on each card: the gaps
        between cards belong to the editor underneath, so clicking beside a
        video still puts the caret where you clicked. */}
    {renderBlock && slots.length > 0 &&
    <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
      {slots.map((s) => (
        <div key={s.id} style={{ position: 'absolute', left: s.left, top: s.top,
          width: s.width, height: s.height, pointerEvents: 'auto' }}>
          {renderBlock({ key: s.key, line: s.line, width: s.width, height: s.height,
            active: s.line === activeLine, edit: (text) => replaceLine(s.line, text) })}
        </div>))}
    </div>}
    {slash &&
    <MdCmdMenu anchor={slash} items={cmdItems} active={cmdActive}
      light={isLightMD()} accent={accent} onPick={runCmd}
      onHover={(i) => {const o = slashRef.current;if (o && o.idx !== i) putSlash({ ...o, idx: i });}} />}
    {wikiMenu &&
    <MdWikiMenu anchor={wikiMenu} items={wikiItems} active={wikiActive}
      light={isLightMD()} accent={accent} onPick={pickWiki}
      onHover={(i) => {const o = wikiMenuRef.current;if (o && o.idx !== i) putWikiMenu({ ...o, idx: i });}} />}
    {tagMenu &&
    <MdTagMenu anchor={tagMenu} items={tagItems} active={tagActive}
      light={isLightMD()} accent={accent} onPick={pickTag}
      onHover={(i) => {const o = tagMenuRef.current;if (o && o.idx !== i) putTagMenu({ ...o, idx: i });}} />}
    {pasteAs &&
    <MdPasteAs anchor={pasteAs} light={isLightMD()} accent={accent}
      rows={pasteAs.rows} onPick={pickFormat} />}
    {prompt && (prompt.kind === 'database' ?
    <MdDatabasePrompt anchor={prompt} light={isLightMD()} accent={accent}
      onPick={placeDatabase}
      onCancel={() => { setPrompt(null); const el = ref.current; if (el) el.focus(); }} /> :
    <MdEmbedPrompt anchor={prompt} light={isLightMD()} accent={accent}
      onSubmit={placeEmbed}
      onCancel={() => { setPrompt(null); const el = ref.current; if (el) el.focus(); }} />)}
    </div>
  );
}

window.LiveMarkdown = LiveMarkdown;
